import React, { useState, useEffect } from 'react';
import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory, Language } from '../utils/i18n';
import { 
  Calculator, Download, Copy, Check, AlertCircle, ArrowLeft, Sparkles, 
  FileText, Upload, Calendar, Building2, Receipt, ShieldCheck, Users, 
  Trees, Flame, Cpu, MapPin, Coins, RefreshCw, FileCheck, Layers, X
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { autoDownloadReport } from '../utils/autoDownloader';
import TelegramExportButton from './TelegramExportButton';
import ExcelExportButton from './ExcelExportButton';
import DpsXmlExportButton from './DpsXmlExportButton';
import CounterpartyCheckerModal from './CounterpartyCheckerModal';
import { getFieldLabel, getFieldValue, getNotesText } from '../utils/demoPresetsI18n';
import { ClickableReportViewer } from './ClickableReportViewer';

interface TaxAgentViewProps {
  agentId: string;
  language: Language;
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
  onLanguageChange?: (lang: Language) => void;
}

const DEFAULT_DEMO_DATA: Record<string, {
  field1LabelUk: string; field1Value: string;
  field2LabelUk: string; field2Value: string;
  field3LabelUk: string; field3Value: string;
  field4LabelUk: string; field4Value: string;
  field5LabelUk: string; field5Value: string;
  field6LabelUk: string; field6Value: string;
  field1LabelPl?: string; field1ValuePl?: string;
  field2LabelPl?: string; field2ValuePl?: string;
  field3LabelPl?: string; field3ValuePl?: string;
  field4LabelPl?: string; field4ValuePl?: string;
  field5LabelPl?: string; field5ValuePl?: string;
  field6LabelPl?: string; field6ValuePl?: string;
  field1LabelEn?: string; field1ValueEn?: string;
  field2LabelEn?: string; field2ValueEn?: string;
  field3LabelEn?: string; field3ValueEn?: string;
  field4LabelEn?: string; field4ValueEn?: string;
  field5LabelEn?: string; field5ValueEn?: string;
  field6LabelEn?: string; field6ValueEn?: string;
  notesTextUk: string;
  notesTextPl?: string;
  notesTextEn?: string;
}> = {
  'tax-pn-unblocker-auditor': {
    field1LabelUk: 'Номер та дата зупиненої ПН/РК & Сума з ПДВ',
    field1ValueUk: 'Податкова накладна № 148 від 12.08.2026 / Сума: 1 450 000.00 грн (в т.ч. ПДВ 20%: 241 666.67 грн)',
    field1LabelPl: 'Numer i data wstrzymanej faktury VAT (PN/RK) & Kwota z VAT',
    field1ValuePl: 'Faktura VAT (PN) Nr 148 z dnia 12.08.2026 / Kwota: 1 450 000.00 UAH (w tym VAT 20%: 241 666.67 UAH)',
    field1LabelEn: 'Suspended VAT Invoice (PN/RK) No. & Date & Total with VAT',
    field1ValueEn: 'VAT Invoice No. 148 dated 12.08.2026 / Total: 1,450,000.00 UAH (incl. 20% VAT: 241,666.67 UAH)',
    field1Value: 'Податкова накладна № 148 від 12.08.2026 / Сума: 1 450 000.00 грн (в т.ч. ПДВ 20%: 241 666.67 грн)',

    field2LabelUk: 'Код УКТ ЗЕД / ДКПП & Зміст господарської операції',
    field2ValueUk: 'Код УКТ ЗЕД 7214 20 00 00 (Арматура сталева гарячекатана Ø12 мм) / Поставка за договором купівлі-продажу будівельних матеріалів',
    field2LabelPl: 'Kod towaru CN/UKT ZED & Treść transakcji gospodarczej',
    field2ValuePl: 'Kod CN/UKT ZED 7214 20 00 00 (Pręty zbrojeniowe ze stali Ø12 mm) / Dostawa materiałów budowlanych',
    field2LabelEn: 'HS/UKT ZED Product Code & Commercial Transaction Description',
    field2ValueEn: 'HS/UKT ZED Code 7214 20 00 00 (Hot-rolled steel rebar Ø12 mm) / Supply of construction materials',
    field2Value: 'Код УКТ ЗЕД 7214 20 00 00 (Арматура сталева гарячекатана Ø12 мм) / Поставка за договором купівлі-продажу будівельних матеріалів',

    field3LabelUk: 'Критерій ризиковості за Постановою КМУ № 1165',
    field3ValueUk: 'п. 1 Критеріїв ризиковості здійснення операцій (Перевищення обсягу постачання товару 7214 над обсягом придбання у віртуальному залишку ДПС > 1.5 разу)',
    field3LabelPl: 'Kryterium ryzyka wg Uchwały Rady Ministrów nr 1165',
    field3ValuePl: 'pkt 1 Kryteriów ryzyka operacji (nadwyżka wolumenu dostawy nad wolumenem nabycia w wirtualnym stanie magazynowym DPS > 1.5)',
    field3LabelEn: 'Risk Criterion under CMU Resolution No. 1165',
    field3ValueEn: 'p. 1 Transaction Risk Criteria (Volume of supplied goods exceeds purchase volume in DPS virtual balance by > 1.5x)',
    field3Value: 'п. 1 Критеріїв ризиковості здійснення операцій (Перевищення обсягу постачання товару 7214 над обсягом придбання у віртуальному залишку ДПС > 1.5 разу)',

    field4LabelUk: 'Перелік первинних документів (Договір, ТТН, видаткові, оплата)',
    field4ValueUk: 'Договір поставки № 28/07-26 від 28.07.2026, Специфікація № 1, Видаткова накладна № ВН-148 від 12.08.2026, ТТН № 148-Т від 12.08.2026 (перевізник ТОВ «Транс-Буд»), Сертифікат відповідності № UA.041.00248, Платіжне доручення № 892 від 13.08.2026, Вхідні видаткові накладні від заводу-виробника ПрАТ «АрселорМіттал Кривий Ріг»',
    field4LabelPl: 'Wykaz dokumentów źródłowych (Umowa, List przewozowy, WZ, Płatność)',
    field4ValuePl: 'Umowa dostawy nr 28/07-26 z dnia 28.07.2026, Specyfikacja nr 1, Faktura WZ nr 148, List przewozowy TTN nr 148-T, Certyfikat jakości nr UA.041.00248, Wyciąg bankowy nr 892',
    field4LabelEn: 'Primary Documents Register (Contract, Waybills, Invoices, Payment)',
    field4ValueEn: 'Supply Contract No. 28/07-26 dtd 28.07.2026, Spec No. 1, Delivery Note VN-148, Consignment Note TTN-148-T, Quality Certificate UA.041.00248, Bank Payment Order 892, Manufacturer Source Invoices',
    field4Value: 'Договір поставки № 28/07-26 від 28.07.2026, Специфікація № 1, Видаткова накладна № ВН-148 від 12.08.2026, ТТН № 148-Т від 12.08.2026 (перевізник ТОВ «Транс-Буд»), Сертифікат відповідності № UA.041.00248, Платіжне доручення № 892 від 13.08.2026, Вхідні видаткові накладні від заводу-виробника ПрАТ «АрселорМіттал Кривий Ріг»',

    field5LabelUk: 'Матеріально-технічна база & Персонал (20-ОПП, склади, техніка)',
    field5ValueUk: 'Форма 20-ОПП подана (склад металопрокату 1 200 кв.м, договір оренди № 12/24), кран баштовий, навантажувач вилковий, штатна чисельність — 18 працівників (включаючи водіїв, стропальників, комірників), заборгованість із заробітної плати та ЄСВ відсутня',
    field5LabelPl: 'Baza materiałowo-techniczna & Personel (Formularz 20-OPP, magazyny)',
    field5ValuePl: 'Formularz 20-OPP złożony (magazyn stali 1 200 m², umowa najmu), wózek widłowy, dźwig, 18 zatrudnionych pracowników, brak zaległości podatkowych i ZUS',
    field5LabelEn: 'Material & Technical Assets & Workforce (Form 20-OPP, Warehouses, Staff)',
    field5ValueEn: 'Form 20-OPP filed (1,200 sq.m metal warehouse under lease No. 12/24), forklift, crane, 18 employees on payroll, zero tax or salary arrears',
    field5Value: 'Форма 20-ОПП подана (склад металопрокату 1 200 кв.м, договір оренди № 12/24), кран баштовий, навантажувач вилковий, штатна чисельність — 18 працівників (включаючи водіїв, стропальників, комірників), заборгованість із заробітної плати та ЄСВ відсутня',

    field6LabelUk: 'Контрагент-покупець / постачальник (ЄДРПОУ, статус)',
    field6ValueUk: 'Покупець: ТОВ «Київ-Буд Девелопмент» (ЄДРПОУ 39485721, платник ПДВ, генпідрядник житлового комплексу) ↔ Постачальник: ТОВ «Мет-Пром Трейд» (ЄДРПОУ 41829304)',
    field6LabelPl: 'Kontrahent nabywca / dostawca (NIP/EDRPOU, status)',
    field6ValuePl: 'Nabywca: TOV «Kyiv-Bud Development» (EDRPOU 39485721, czynny płatnik VAT) ↔ Dostawca: TOV «Met-Prom Trade» (EDRPOU 41829304)',
    field6LabelEn: 'Counterparty Buyer / Supplier (EDRPOU, VAT Status)',
    field6ValueEn: 'Buyer: LLC «Kyiv-Bud Development» (EDRPOU 39485721, Active VAT Payer) ↔ Supplier: LLC «Met-Prom Trade» (EDRPOU 41829304)',
    field6Value: 'Покупець: ТОВ «Київ-Буд Девелопмент» (ЄДРПОУ 39485721, платник ПДВ, генпідрядник житлового комплексу) ↔ Постачальник: ТОВ «Мет-Пром Трейд» (ЄДРПОУ 41829304)',

    notesTextUk: 'Скласти вичерпне офіційне Письмове Пояснення з документальним обґрунтуванням до Комісії регіонального рівня ГУ ДПС для прийняття рішення про реєстрацію податкової накладної № 148 від 12.08.2026. Довести реальність господарської операції (наявність джерела походження товару, транспортування, складського зберігання, наявності трудових та матеріальних ресурсів за ф. 20-ОПП), спростувати п. 1 Критеріїв ризиковості за Постановою КМУ № 1165 з посиланням на правові висновки Верховного Суду (постанови у справах № 826/12108/18, № 810/3947/18, № 160/8860/21) та сформувати Реєстр первинних документів для електронного подання (форма J/F1312603) у Word (.docx за ДСТУ 4163:2020).',
    notesTextPl: 'Przygotować wyczerpujące oficjalne pisemne wyjaśnienia wraz z uzasadnieniem dokumentacyjnym do Komisji Regionalnej DPS w celu odblokowania i rejestracji faktury VAT (PN) nr 148 z dnia 12.08.2026. Wykazać realność transakcji gospodarczej, obalić kryterium ryzyka wg Uchwały KMU 1165 z powołaniem się na linię orzeczniczą Sądu Najwyższego oraz wygenerować rejestr dokumentów źródłowych w formacie Word (.docx).',
    notesTextEn: 'Draft comprehensive official written Explanations with documentary evidence to the Regional Commission of the State Tax Service (DPS) to unblock and register VAT invoice No. 148 dated 12.08.2026. Prove economic substance and genuine commercial reality, refute p. 1 Transaction Risk Criteria under Resolution No. 1165 with Supreme Court legal precedents and generate a structured Primary Documents Register in Word (.docx DSTU 4163:2020).',
    notesText: 'Скласти вичерпне офіційне Письмове Пояснення з документальним обґрунтуванням до Комісії регіонального рівня ГУ ДПС для прийняття рішення про реєстрацію податкової накладної № 148 від 12.08.2026. Довести реальність господарської операції (наявність джерела походження товару, транспортування, складського зберігання, наявності трудових та матеріальних ресурсів за ф. 20-ОПП), спростувати п. 1 Критеріїв ризиковості за Постановою КМУ № 1165 з посиланням на правові висновки Верховного Суду (постанови у справах № 826/12108/18, № 810/3947/18, № 160/8860/21) та сформувати Реєстр первинних документів для електронного подання (форма J/F1312603) у Word (.docx за ДСТУ 4163:2020).'
  },
  'tax-transfer-pricing-doc-architect': {
    field1LabelUk: 'Категорія контрольованої операції & Сторони',
    field1Value: 'Експорт агропродукції (кукурудза, пшениця) на пов’язану компанію-нерезидента / ТОВ «Агро-Трейд» ↔ Agro Export SA (Швейцарія)',
    field2LabelUk: 'Цільова юрисдикція & Законодавство (ПКУ / CIT / OECD)',
    field2Value: 'Україна (ст. 39 ПКУ), Польща (art. 11k-t CIT) та Керівні настанови ОЕСР (OECD Guidelines 2022)',
    field3LabelUk: 'Обраний метод ТЦУ & Тестована сторона',
    field3Value: 'Метод чистого прибутку (TNMM / Чиста рентабельність витрат NCP) / Тестована сторона: ТОВ «Агро-Трейд»',
    field4LabelUk: 'Обсяг операції за звітний рік (Валюта / Еквівалент)',
    field4Value: '85 400 000.00 грн (перевищує критерій 10 млн грн за ст. 39.2 ПКУ при загальному доході платника > 150 млн грн)',
    field5LabelUk: 'Розрахований діапазон рентабельності (25% - 75%)',
    field5Value: 'Нижній квартиль: 2.85%, Медіана: 4.60%, Верхній квартиль: 6.90% (Фактична рентабельність: 5.12% — в межах ринку)',
    field6LabelUk: 'Функціональний профіль FAR (Functions, Assets, Risks)',
    field6Value: 'Виробник з обмеженими ризиками (Limited Risk Manufacturer): виробничі активи в Україні, ринковий ризик несе нерезидент',
    notesText: 'Скласти повну та юридично бездоганну Документацію з трансфертного ціноутворення (Local File) за звітний період. Провести детальний функціональний аналіз FAR, обґрунтувати вибір методу чистого прибутку (TNMM), навести формули розрахунку показника чистої рентабельності витрат (Net Cost Plus), підтвердити відповідність цін принципу «витягнутої руки» (Arm\'s Length) та надати рекомендації для Звіту про КО та форми TPR-C у Word (.docx) за ДСТУ 4163:2020 та Excel (.xlsx).'
  },
  'tax-ksef-vat-auditor': {
    field1LabelUk: 'Номер фактури KSeF / Формат файлу',
    field1ValueUk: 'KSeF FA(2) XML / Faktura VAT Nr FV/2026/08/0419 (KSeF ID: 7740001454-20260814-123456-ABCDEF-01)',
    field1ValuePl: 'KSeF FA(2) XML / Faktura VAT Nr FV/2026/08/0419 (KSeF ID: 7740001454-20260814-123456-ABCDEF-01)',
    field1ValueEn: 'KSeF FA(2) XML / VAT Invoice No FV/2026/08/0419 (KSeF ID: 7740001454-20260814-123456-ABCDEF-01)',
    field1Value: 'KSeF FA(2) XML / Faktura VAT Nr FV/2026/08/0419 (KSeF ID: 7740001454-20260814-123456-ABCDEF-01)',
    field2LabelUk: 'Сторони операції (Sprzedawca ↔ Nabywca & NIP)',
    field2ValueUk: 'Pol-Trans Sp. z o.o. (NIP 5252525252) ↔ Euro-Logistics S.A. (NIP 7740001454) / Status: Czynny podatnik VAT (Biała Lista)',
    field2ValuePl: 'Pol-Trans Sp. z o.o. (NIP 5252525252) ↔ Euro-Logistics S.A. (NIP 7740001454) / Status: Czynny podatnik VAT (Biała Lista)',
    field2ValueEn: 'Pol-Trans Sp. z o.o. (Tax ID 5252525252) ↔ Euro-Logistics S.A. (Tax ID 7740001454) / Active VAT Payer (White List)',
    field2Value: 'Pol-Trans Sp. z o.o. (NIP 5252525252) ↔ Euro-Logistics S.A. (NIP 7740001454) / Status: Czynny podatnik VAT (Biała Lista)',
    field3LabelUk: 'Перевірка рахунку в Wykazie VAT (Biała Lista)',
    field3ValueUk: 'PL 40 1240 1066 1111 0011 5632 9830 (Rachunek rozliczeniowy zweryfikowany na Białej Liście MF, status: Zgodny)',
    field3ValuePl: 'PL 40 1240 1066 1111 0011 5632 9830 (Rachunek rozliczeniowy zweryfikowany na Białej Liście MF, status: Zgodny)',
    field3ValueEn: 'PL 40 1240 1066 1111 0011 5632 9830 (Settlement account verified on Ministry White List, status: Matched)',
    field3Value: 'PL 40 1240 1066 1111 0011 5632 9830 (Rachunek rozliczeniowy zweryfikowany na Białej Liście MF, status: Zgodny)',
    field4LabelUk: 'Коди товарних груп GTU & Спецпроцедури',
    field4ValueUk: 'GTU_12 (Usługi niematerialne / doradcze) + Procedura MPP (Split Payment) + Transakcja krajowa VAT 23%',
    field4ValuePl: 'GTU_12 (Usługi niematerialne / doradcze) + Procedura MPP (Split Payment) + Transakcja krajowa VAT 23%',
    field4ValueEn: 'GTU_12 (Intangible advisory services) + MPP Split Payment Procedure + Domestic 23% VAT rate',
    field4Value: 'GTU_12 (Usługi niematerialne / doradcze) + Procedura MPP (Split Payment) + Transakcja krajowa VAT 23%',
    field5LabelUk: 'Сума Netto, VAT та Brutto фактури',
    field5ValueUk: 'Netto: 45 000.00 PLN / VAT 23%: 10 350.00 PLN / Brutto: 55 350.00 PLN (Mechanizm Podzielonej Płatności)',
    field5ValuePl: 'Netto: 45 000.00 PLN / VAT 23%: 10 350.00 PLN / Brutto: 55 350.00 PLN (Mechanizm Podzielonej Płatności)',
    field5ValueEn: 'Net: 45,000.00 PLN / VAT 23%: 10,350.00 PLN / Gross: 55,350.00 PLN (Split Payment Mechanism)',
    field5Value: 'Netto: 45 000.00 PLN / VAT 23%: 10 350.00 PLN / Brutto: 55 350.00 PLN (Mechanizm Podzielonej Płatności)',
    field6LabelUk: 'Узгодження зі структурою JPK_V7M / JPK_V7K',
    field6ValueUk: 'Узгоджено з полями P_13_1, P_14_1 JPK_V7M. Ризики невизнання податкового кредиту відсутні.',
    field6ValuePl: 'Zgodność z polami P_13_1, P_14_1 JPK_V7M. Brak ryzyk odliczenia podatku naliczonego.',
    field6ValueEn: 'Reconciled with JPK_V7M fields P_13_1, P_14_1. Input VAT deduction risks eliminated.',
    field6Value: 'Zgodność z polami P_13_1, P_14_1 JPK_V7M. Brak ryzyk odliczenia podatku naliczonego.',
    notesTextUk: 'Здійснити технічний, логічний та податковий аудит електронної структурованої фактури KSeF FA(2). Перевірити валідність XML за схемою Міністерства фінансів Польщі, звірити банківський рахунок покупця/продавця з офіційним реєстром Biała Lista Podatników VAT, проконтролювати коректність застосування коду GTU_12 та механізму Split Payment (MPP) і згенерувати протокол аудиту KSeF у Word (.docx) за ДСТУ 4163:2020 та Excel (.xlsx).',
    notesTextPl: 'Przeprowadzić audyt techniczny, logiczny i podatkowy faktury ustrukturyzowanej KSeF FA(2). Zweryfikować poprawność schematu XML, stawki VAT, NIP kontrahenta i rachunek na Białej Liście VAT, mechanizm podzielonej płatności (MPP) oraz spójność z plikiem JPK_V7M. Wygenerować raport z audytu w formacie Word (.docx) i plik XML.',
    notesTextEn: 'Execute technical and tax audit of structured e-invoice KSeF FA(2). Validate XML schema, tax rates, counterparty Tax ID on Polish White List, mandatory MPP split payment and reconciliation with JPK_V7 filing in Word (.docx) and XML.',
    notesText: 'Przeprowadzić audyt techniczny, logiczny i podatkowy faktury ustrukturyzowanej KSeF FA(2). Zweryfikować poprawność schematu XML, stawki VAT, NIP kontrahenta i rachunek na Białej Liście VAT, mechanizm podzielonej płatności (MPP) oraz spójność z plikiem JPK_V7M. Wygenerować raport z audytu w formacie Word (.docx) i plik XML.'
  },
  'it-fatturapa-sdi-auditor': {
    field1LabelUk: 'Номер та формат файлу FatturaPA XML',
    field1ValueUk: 'FatturaPA XML (FPR12 v1.2.2) / Fattura Elettronica Nr IT-2026/084 (SDI ID: 98471203)',
    field1ValuePl: 'FatturaPA XML (FPR12 v1.2.2) / Faktura Nr IT-2026/084 (SDI ID: 98471203)',
    field1ValueEn: 'FatturaPA XML (FPR12 v1.2.2) / Invoice No IT-2026/084 (SDI Identifier: 98471203)',
    field1Value: 'FatturaPA XML (FPR12 v1.2.2) / Fattura Elettronica Nr IT-2026/084 (SDI ID: 98471203)',
    field2LabelUk: 'Дані сторін: Cedente/Prestatore ↔ Cessionario/Committente',
    field2ValueUk: 'Milano Tech S.r.l. (P.IVA 01234567890) ↔ Roma Pubblica Amministrazione (CF 80012345678)',
    field2ValuePl: 'Milano Tech S.r.l. (P.IVA 01234567890) ↔ Roma Pubblica Amministrazione (CF 80012345678)',
    field2ValueEn: 'Milano Tech S.r.l. (VAT 01234567890) ↔ Roma Public Administration (Fiscal Code 80012345678)',
    field2Value: 'Milano Tech S.r.l. (P.IVA 01234567890) ↔ Roma Pubblica Amministrazione (CF 80012345678)',
    field3LabelUk: 'Канал доставки SDI (Codice Destinatario / PEC)',
    field3ValueUk: 'Codice Destinatario SDI: M5UXCR1 (Canale accreditato SdICoop / Agenzia delle Entrate)',
    field3ValuePl: 'Codice Destinatario SDI: M5UXCR1 (Kanał SdICoop / Agenzia delle Entrate)',
    field3ValueEn: 'SDI Recipient Code: M5UXCR1 (Certified SdICoop Channel / Agenzia delle Entrate)',
    field3Value: 'Codice Destinatario SDI: M5UXCR1 (Canale accreditato SdICoop / Agenzia delle Entrate)',
    field4LabelUk: 'Режим оплати та Split Payment PA (Scissione)',
    field4ValueUk: 'Esigibilità IVA: S (Scissione dei pagamenti / Split Payment PA) — ст. 17-ter DPR 633/72',
    field4ValuePl: 'Rozliczenie VAT: S (Split Payment PA / Scissione dei pagamenti) — art. 17-ter DPR 633/72',
    field4ValueEn: 'VAT Modality: S (Public Sector Split Payment / Scissione dei pagamenti DPR 633/72)',
    field4Value: 'Esigibilità IVA: S (Scissione dei pagamenti / Split Payment PA) — ст. 17-ter DPR 633/72',
    field5LabelUk: 'Суми Imponibile, Aliquota IVA та Totale Documento',
    field5ValueUk: 'Imponibile: €42 500.00 / IVA 22%: €9 350.00 / Totale: €51 850.00 (Netto a pagare: €42 500.00)',
    field5ValuePl: 'Podstawa: 42 500.00 € / IVA 22%: 9 350.00 € / Suma: 51 850.00 € (Do zapłaty: 42 500.00 €)',
    field5ValueEn: 'Taxable: €42,500.00 / IVA 22%: €9,350.00 / Gross Total: €51,850.00 (Payable Net: €42,500.00)',
    field5Value: 'Imponibile: €42 500.00 / IVA 22%: €9 350.00 / Totale: €51 850.00 (Netto a pagare: €42 500.00)',
    field6LabelUk: 'Розрахунок податкового платежу Modello F24',
    field6ValueUk: 'Sezione Erario: Codice Tributo 6008 (Versamento IVA mensile agosto) — Сума: €9 350.00',
    field6ValuePl: 'Sekcja Erario F24: Kod 6008 (Płatność podatku IVA za sierpień) — Kwota: 9 350.00 €',
    field6ValueEn: 'Modello F24 Erario Section: Tax Code 6008 (Monthly IVA August Settlement) — Amount: €9,350.00',
    field6Value: 'Sezione Erario: Codice Tributo 6008 (Versamento IVA mensile agosto) — Сума: €9 350.00',
    notesTextUk: 'Провести технічний та податковий аудит італійського електронного рахунку FatturaPA XML за офіційними правилами SDI Agenzia delle Entrate. Перевірити коректність коду Codice Destinatario, застосування режиму Split Payment PA, звірити податкові ставки IVA (22%) та згенерувати протокол валідації і розрахунок форми Modello F24 у Word (.docx) та Excel (.xlsx).',
    notesTextPl: 'Przeprowadzić audyt techniczny i podatkowy włoskiej faktury elektronicznej FatturaPA XML wg wytycznych SDI. Zweryfikować poprawność kodu Codice Destinatario, procedury Split Payment PA, stawek IVA 22% oraz wygenerować raport z formularzem Modello F24 w Word (.docx) i Excel (.xlsx).',
    notesTextEn: 'Execute technical and tax validation of Italian FatturaPA XML e-invoice per Agenzia delle Entrate SDI rules. Validate Codice Destinatario, verify Split Payment PA compliance, check 22% IVA calculations, and generate Modello F24 tax settlement report in Word (.docx) and Excel (.xlsx).'
  },
  'es-facturae-verifactu-auditor': {
    field1LabelUk: 'Формат та версія FacturaE XML / Veri*Factu',
    field1ValueUk: 'FacturaE v3.2.2 XML / Factura Comercial FC-2026-0912 / Підпис XAdES-EPES',
    field1ValuePl: 'FacturaE v3.2.2 XML / Faktura FC-2026-0912 / Podpis cyfrowy XAdES-EPES',
    field1ValueEn: 'FacturaE v3.2.2 XML / Commercial Invoice FC-2026-0912 / XAdES-EPES Digital Signature',
    field1Value: 'FacturaE v3.2.2 XML / Factura Comercial FC-2026-0912 / Підпис XAdES-EPES',
    field2LabelUk: 'Реквізити Emisor ↔ Receptor (NIF/CIF AEAT)',
    field2ValueUk: 'Iberia Solar SL (NIF B87654321) ↔ Construcciones Madrid SA (NIF A12345678)',
    field2ValuePl: 'Iberia Solar SL (NIF B87654321) ↔ Construcciones Madrid SA (NIF A12345678)',
    field2ValueEn: 'Iberia Solar SL (Tax ID B87654321) ↔ Construcciones Madrid SA (Tax ID A12345678)',
    field2Value: 'Iberia Solar SL (NIF B87654321) ↔ Construcciones Madrid SA (NIF A12345678)',
    field3LabelUk: 'Ланцюг валідації Veri*Factu / QR-код та хеш',
    field3ValueUk: 'Huella digital SHA-256: e3b0c44298fc1c149afbf4c8996fb924 / QR-код верифікований в AEAT',
    field3ValuePl: 'Skrót SHA-256: e3b0c44298fc1c149afbf4c8996fb924 / Kod QR zweryfikowany w AEAT',
    field3ValueEn: 'SHA-256 Record Hash: e3b0c44298fc1c149afbf4c8996fb924 / Veri*Factu AEAT QR Code Verified',
    field3Value: 'Huella digital SHA-256: e3b0c44298fc1c149afbf4c8996fb924 / QR-код верифікований в AEAT',
    field4LabelUk: 'Ставки IVA, Ретенції IRPF та Recargo',
    field4ValueUk: 'Base Imponible: €68 000.00 / IVA 21%: €14 280.00 / Retención IRPF 15%: -€10 200.00',
    field4ValuePl: 'Baza: 68 000.00 € / IVA 21%: 14 280.00 € / Potrącenie IRPF 15%: -10 200.00 €',
    field4ValueEn: 'Taxable Base: €68,000.00 / IVA 21%: €14,280.00 / IRPF Withholding 15%: -€10,200.00',
    field4Value: 'Base Imponible: €68 000.00 / IVA 21%: €14 280.00 / Retención IRPF 15%: -€10 200.00',
    field5LabelUk: 'Підсумкова сума до сплати (Total a Pagar)',
    field5ValueUk: 'Total Importe Bruto: €82 280.00 / Total a Pagar con Retención: €72 080.00 EUR',
    field5ValuePl: 'Kwota Brutto: 82 280.00 € / Do zapłaty po potrąceniu: 72 080.00 € EUR',
    field5ValueEn: 'Gross Total: €82,280.00 / Net Payable with Withholding: €72,080.00 EUR',
    field5Value: 'Total Importe Bruto: €82 280.00 / Total a Pagar con Retención: €72 080.00 EUR',
    field6LabelUk: 'Податкові декларації AEAT (Modelo 303 / 111)',
    field6ValueUk: 'Modelo 303 (IVA General casilla 03: €14 280.00) + Modelo 111 (Retenciones IRPF casilla 06: €10 200.00)',
    field6ValuePl: 'Modelo 303 (Deklaracja IVA poz. 03: 14 280.00 €) + Modelo 111 (Zaliczka IRPF poz. 06: 10 200.00 €)',
    field6ValueEn: 'Modelo 303 (General IVA Box 03: €14,280.00) + Modelo 111 (IRPF Withholding Box 06: €10,200.00)',
    field6Value: 'Modelo 303 (IVA General casilla 03: €14 280.00) + Modelo 111 (Retenciones IRPF casilla 06: €10 200.00)',
    notesTextUk: 'Здійснити аудит іспанського електронного рахунку FacturaE 3.2.2 XML та відповідності вимогам Veri*Factu (Ley Crea y Crece). Перевірити цифровий підпис XAdES, NIF/CIF контрагентів у системі AEAT, правильність розрахунку податку IVA (21%) та утримання прибуткового податку IRPF (15%) і сформувати звіт для декларацій Modelo 303 та Modelo 111 у Word (.docx) та Excel (.xlsx).',
    notesTextPl: 'Przeprowadzić audyt hiszpańskiej e-faktury FacturaE 3.2.2 XML i zgodności z systemem Veri*Factu. Sprawdzić podpis cyfrowy XAdES, numery NIF w AEAT, kalkulację podatku IVA (21%) i potrąceń IRPF (15%) oraz sporządzić raport dla deklaracji Modelo 303 i Modelo 111 w Word (.docx) i Excel (.xlsx).',
    notesTextEn: 'Audit Spanish FacturaE 3.2.2 XML e-invoice and Veri*Factu compliance under Ley Crea y Crece. Validate XAdES signature, check AEAT NIF registries, verify 21% IVA calculations and 15% IRPF withholdings, and compile Modelo 303 / 111 tax audit ledger in Word (.docx) and Excel (.xlsx).'
  },
  'pt-saft-fatura-auditor': {
    field1LabelUk: 'Файл SAF-T (PT) XML / Версія структури',
    field1ValueUk: 'SAF-T (PT) v1.04_01 XML / Período 2026-08 / SalesInvoices & WorkingDocuments',
    field1ValuePl: 'Plik SAF-T (PT) v1.04_01 XML / Okres 2026-08 / Ewidencja faktur sprzedaży',
    field1ValueEn: 'SAF-T (PT) v1.04_01 XML / Monthly Period 2026-08 / Sales Invoices Ledger',
    field1Value: 'SAF-T (PT) v1.04_01 XML / Período 2026-08 / SalesInvoices & WorkingDocuments',
    field2LabelUk: 'Електронна фактура & Код валідації ATCUD',
    field2ValueUk: 'Fatura FT 2026/1458 / ATCUD: CS928104-1458 / QR Code AT verificado',
    field2ValuePl: 'Faktura FT 2026/1458 / Kod ATCUD: CS928104-1458 / Kod QR AT poprawny',
    field2ValueEn: 'Invoice FT 2026/1458 / ATCUD Code: CS928104-1458 / Tax Authority QR Code Verified',
    field2Value: 'Fatura FT 2026/1458 / ATCUD: CS928104-1458 / QR Code AT verificado',
    field3LabelUk: 'Ідентифікатори NIF Emissor ↔ NIF Adquirente',
    field3ValueUk: 'Lisboa Tech Lda (NIF PT501234567) ↔ Porto Logística SA (NIF PT509876543)',
    field3ValuePl: 'Lisboa Tech Lda (NIF PT501234567) ↔ Porto Logística SA (NIF PT509876543)',
    field3ValueEn: 'Lisboa Tech Lda (Tax ID PT501234567) ↔ Porto Logística SA (Tax ID PT509876543)',
    field3Value: 'Lisboa Tech Lda (NIF PT501234567) ↔ Porto Logística SA (NIF PT509876543)',
    field4LabelUk: 'Регіон та ставки португальського IVA',
    field4ValueUk: 'Portugal Continental: Taxa Normal IVA 23% (€11 500.00 на базу €50 000.00 EUR)',
    field4ValuePl: 'Portugalia Kontynentalna: Stawka podstawowa IVA 23% (11 500.00 € od bazy 50 000.00 €)',
    field4ValueEn: 'Mainland Portugal: Standard IVA 23% Rate (€11,500.00 on €50,000.00 Taxable Base)',
    field4Value: 'Portugal Continental: Taxa Normal IVA 23% (€11 500.00 на базу €50 000.00 EUR)',
    field5LabelUk: 'Контрольні суми (Hash de Controlo)',
    field5ValueUk: 'Assinatura RSA-SHA1: V1p9K... / Validação sequencial do Hash anterior: Sucesso 100%',
    field5ValuePl: 'Podpis RSA-SHA1: V1p9K... / Weryfikacja ciągłości skrótów: Zgodna 100%',
    field5ValueEn: 'RSA-SHA1 Digital Signature: V1p9K... / Previous Record Hash Chain Continuity: 100% Valid',
    field5Value: 'Assinatura RSA-SHA1: V1p9K... / Validação sequencial do Hash anterior: Sucesso 100%',
    field6LabelUk: 'Розрахунок податкового платежу (Guia AT)',
    field6ValueUk: 'Declaração Periódica do IVA: Campo 03 (IVA liquidado: €11 500.00) / Emissão de Guia de Pagamento',
    field6ValuePl: 'Deklaracja okresowa IVA: Pole 03 (Podatek należny: 11 500.00 €) / Generowanie wpłaty AT',
    field6ValueEn: 'Periodic VAT Declaration: Field 03 (Output IVA: €11,500.00) / Tax Payment Document (Guia AT)',
    field6Value: 'Declaração Periódica do IVA: Campo 03 (IVA liquidado: €11 500.00) / Emissão de Guia de Pagamento',
    notesTextUk: 'Провести комплексний аудит стандартного файлу податкового аудиту SAF-T (PT) XML 1.04_01 та електронних рахунків-фактур згідно з вимогами Autoridade Tributária e Aduaneira Португалії. Перевірити коректність коду валідації ATCUD, нерозривність ланцюга цифрових хешів RSA, правильність ставок податку IVA (23%) та підготувати звіт податкового комплаєнсу у Word (.docx) та Excel (.xlsx).',
    notesTextPl: 'Przeprowadzić audyt portugalskiego pliku SAF-T (PT) XML 1.04_01 oraz faktur elektronicznych zgodnie z normami Autoridade Tributária. Sprawdzić unikalny kod ATCUD, ciągłość łańcucha kryptograficznego RSA, stawki IVA 23% i wygenerować raport podatkowy w Word (.docx) i Excel (.xlsx).',
    notesTextEn: 'Execute comprehensive audit of Portuguese SAF-T (PT) XML 1.04_01 tax file and e-invoices under Autoridade Tributária standards. Validate ATCUD series code, RSA cryptographic hash continuity, verify 23% IVA rates, and compile compliance audit report in Word (.docx) and Excel (.xlsx).'
  },
  'se-peppol-svefaktura-auditor': {
    field1LabelUk: 'Формат та версія електронного інвойсу Швеції',
    field1ValueUk: 'PEPPOL BIS Billing 3.0 (UBL 2.1) / Svefaktura / Faktura SE-2026-8812',
    field1ValuePl: 'PEPPOL BIS Billing 3.0 (UBL 2.1) / Svefaktura / Faktura SE-2026-8812',
    field1ValueEn: 'PEPPOL BIS Billing 3.0 (UBL 2.1) / Svefaktura / Invoice SE-2026-8812',
    field1Value: 'PEPPOL BIS Billing 3.0 (UBL 2.1) / Svefaktura / Faktura SE-2026-8812',
    field2LabelUk: 'Організаційні номери Org.nr та реквізити',
    field2ValueUk: 'Nordic Build AB (Org.nr 556123-4567 / Godkänd för F-skatt) ↔ Stockholm Fastigheter AB (Org.nr 556987-6543)',
    field2ValuePl: 'Nordic Build AB (Org.nr 556123-4567 / Status F-skatt) ↔ Stockholm Fastigheter AB (Org.nr 556987-6543)',
    field2ValueEn: 'Nordic Build AB (Org.nr 556123-4567 / F-tax Registered) ↔ Stockholm Fastigheter AB (Org.nr 556987-6543)',
    field2Value: 'Nordic Build AB (Org.nr 556123-4567 / Godkänd för F-skatt) ↔ Stockholm Fastigheter AB (Org.nr 556987-6543)',
    field3LabelUk: 'Специфікація Schematron & Валідація DIGG',
    field3ValueUk: 'Правила Schematron OpenPEPPOL v3.0: 0 помилок, 0 зауважень (100% комплаєнс DIGG/SFTI)',
    field3ValuePl: 'Reguły Schematron OpenPEPPOL v3.0: 0 błędów, 0 ostrzeżeń (100% zgodności DIGG/SFTI)',
    field3ValueEn: 'OpenPEPPOL Schematron Rules v3.0: 0 Errors, 0 Warnings (100% DIGG/SFTI Compliance)',
    field3Value: 'Правила Schematron OpenPEPPOL v3.0: 0 помилок, 0 зауважень (100% комплаєнс DIGG/SFTI)',
    field4LabelUk: 'Ставки шведського ПДВ (Moms) та Omvänd byggmoms',
    field4ValueUk: 'Beskattningsunderlag: 250 000 kr SEK / Moms 25%: 62 500 kr SEK (Totalt: 312 500 kr SEK)',
    field4ValuePl: 'Podstawa: 250 000 kr SEK / Moms 25%: 62 500 kr SEK (Łącznie: 312 500 kr SEK)',
    field4ValueEn: 'Taxable Amount: 250,000 kr SEK / Moms 25%: 62,500 kr SEK (Total: 312,500 kr SEK)',
    field4Value: 'Beskattningsunderlag: 250 000 kr SEK / Moms 25%: 62 500 kr SEK (Totalt: 312 500 kr SEK)',
    field5LabelUk: 'Реквізити Bankgiro / PlusGiro та OCR-номер',
    field5ValueUk: 'Bankgiro: 5123-4567 / OCR-referens: 2026881245 / Valuta: SEK',
    field5ValuePl: 'Bankgiro: 5123-4567 / Numer referencyjny OCR: 2026881245 / Waluta: SEK',
    field5ValueEn: 'Bankgiro: 5123-4567 / OCR Payment Reference: 2026881245 / Currency: SEK',
    field5Value: 'Bankgiro: 5123-4567 / OCR-referens: 2026881245 / Valuta: SEK',
    field6LabelUk: 'Підсумок для декларації Momsdeklaration Skatteverket',
    field6ValueUk: 'Ruta 05 (Försäljning 25% moms: 250 000 kr) + Ruta 10 (Utgående moms 25%: 62 500 kr SEK)',
    field6ValuePl: 'Pole 05 (Sprzedaż 25% moms: 250 000 kr) + Pole 10 (Podatek należny 25%: 62 500 kr SEK)',
    field6ValueEn: 'Box 05 (Sales at 25% VAT: 250,000 kr) + Box 10 (Output VAT 25%: 62,500 kr SEK)',
    field6Value: 'Ruta 05 (Försäljning 25% moms: 250 000 kr) + Ruta 10 (Utgående moms 25%: 62 500 kr SEK)',
    notesTextUk: 'Здійснити технічний та нормативний аудит шведського електронного інвойсу PEPPOL BIS Billing 3.0 / Svefaktura XML. Перевірити валідність бізнес-правил Schematron DIGG, реєстраційний номер Org.nr, статус платника F-skatt в Skatteverket, правильність нарахування 25% Moms та сформувати підсумкову відомість податкової декларації у Word (.docx) та Excel (.xlsx).',
    notesTextPl: 'Przeprowadzić audyt techniczny szwedzkiej e-faktury PEPPOL BIS Billing 3.0 / Svefaktura XML. Zweryfikować zgodność ze schematami Schematron DIGG, numer rejestrowy Org.nr, status F-skatt w Skatteverket, rozliczenie 25% Moms oraz wygenerować raport do deklaracji w Word (.docx) i Excel (.xlsx).',
    notesTextEn: 'Execute technical and regulatory audit of Swedish PEPPOL BIS Billing 3.0 / Svefaktura XML invoice. Validate Schematron DIGG business rules, verify Org.nr and F-skatt status with Skatteverket, calculate 25% Moms tax obligations, and generate Momsdeklaration tax return summary in Word (.docx) and Excel (.xlsx).'
  },
  'de-xrechnung-zugferd-auditor': {
    field1LabelUk: 'Стандарт та версія файлу XRechnung / ZUGFeRD',
    field1ValueUk: 'XRechnung v3.0.1 (CII XML / EN 16931) / Rechnungs-Nr: DE-2026-04192',
    field1ValuePl: 'XRechnung v3.0.1 (CII XML / EN 16931) / Nr faktury: DE-2026-04192',
    field1ValueEn: 'XRechnung v3.0.1 (CII XML / EN 16931) / Invoice No: DE-2026-04192',
    field1Value: 'XRechnung v3.0.1 (CII XML / EN 16931) / Rechnungs-Nr: DE-2026-04192',
    field2LabelUk: 'Маршрутний ідентифікатор замовника (Leitweg-ID)',
    field2ValueUk: 'Leitweg-ID: 991-33455-89 (Bundesbehörde / Міністерство інфраструктури ФРН)',
    field2ValuePl: 'Leitweg-ID: 991-33455-89 (Organ publiczny / Ministerstwo Infrastruktury RFN)',
    field2ValueEn: 'Leitweg-ID: 991-33455-89 (Federal Public Buyer / German Ministry of Infrastructure)',
    field2Value: 'Leitweg-ID: 991-33455-89 (Bundesbehörde / Міністерство інфраструктури ФРН)',
    field3LabelUk: 'Податкові номери Steuernummer & USt-IdNr',
    field3ValueUk: 'Verkäufer: Berlin Engineering GmbH (USt-IdNr: DE123456789 / Steuernummer: 27/123/45678)',
    field3ValuePl: 'Sprzedawca: Berlin Engineering GmbH (USt-IdNr: DE123456789 / Steuernummer: 27/123/45678)',
    field3ValueEn: 'Seller: Berlin Engineering GmbH (VAT ID: DE123456789 / Tax Number: 27/123/45678)',
    field3Value: 'Verkäufer: Berlin Engineering GmbH (USt-IdNr: DE123456789 / Steuernummer: 27/123/45678)',
    field4LabelUk: 'База оподаткування та ставки німецького ПДВ (Umsatzsteuer)',
    field4ValueUk: 'Nettobetrag: €95 000.00 / USt 19%: €18 050.00 / Bruttobetrag: €113 050.00 EUR',
    field4ValuePl: 'Netto: 95 000.00 € / USt 19%: 18 050.00 € / Brutto: 113 050.00 € EUR',
    field4ValueEn: 'Net Amount: €95,000.00 / VAT 19%: €18,050.00 / Gross Total: €113,050.00 EUR',
    field4Value: 'Nettobetrag: €95 000.00 / USt 19%: €18 050.00 / Bruttobetrag: €113 050.00 EUR',
    field5LabelUk: 'Валідація KoSIT Schematron & B2G стандарту',
    field5ValueUk: 'KoSIT Validator: Стан валідації Успішний (0 помилок за правилами BR-DE-01 - BR-DE-28)',
    field5ValuePl: 'Walidator KoSIT: Wynik Pozytywny (0 błędów reguł BR-DE-01 - BR-DE-28)',
    field5ValueEn: 'KoSIT Validator: Validation Successful (0 errors across business rules BR-DE-01 - BR-DE-28)',
    field5Value: 'KoSIT Validator: Стан валідації Успішний (0 помилок за правилами BR-DE-01 - BR-DE-28)',
    field6LabelUk: 'Розрахунок до декларації USt-Voranmeldung (ELSTER)',
    field6ValueUk: 'Kennziffer 81 (Steuerpflichtige Umsätze 19%: €95 000.00) / Steuerbetrag: €18 050.00 EUR',
    field6ValuePl: 'Pozycja 81 (Obrót opodatkowany 19%: 95 000.00 €) / Kwota podatku: 18 050.00 € EUR',
    field6ValueEn: 'ELSTER Code 81 (Taxable turnover 19%: €95,000.00) / Output VAT: €18,050.00 EUR',
    field6Value: 'Kennziffer 81 (Steuerpflichtige Umsätze 19%: €95 000.00) / Steuerbetrag: €18 050.00 EUR',
    notesTextUk: 'Здійснити технічний та податковий аудит німецького електронного рахунку XRechnung 3.0 XML за правилами KoSIT та директивою B2G. Перевірити коректність маршрутного коду Leitweg-ID, валідувати номери USt-IdNr та Steuernummer за базою BZSt, перевірити розрахунок німецького ПДВ 19% та сформувати протокол аудиту для подачі USt-Voranmeldung у Word (.docx) та Excel (.xlsx).',
    notesTextPl: 'Przeprowadzić audyt techniczny i podatkowy niemieckiej faktury elektronicznej XRechnung 3.0 XML wg reguł KoSIT. Sprawdzić poprawność kodu Leitweg-ID, numery USt-IdNr w bazie BZSt, stawkę VAT 19% oraz wygenerować raport do deklaracji USt-Voranmeldung w Word (.docx) i Excel (.xlsx).',
    notesTextEn: 'Execute technical and tax audit of German XRechnung 3.0 XML e-invoice according to KoSIT business rules. Validate B2G routing code Leitweg-ID, verify USt-IdNr and Steuernummer via BZSt, check 19% German VAT computation, and prepare USt-Voranmeldung tax return schedule in Word (.docx) and Excel (.xlsx).'
  },
  'fr-facturx-chorus-auditor': {
    field1LabelUk: 'Формат та профіль Factur-X / Chorus Pro',
    field1ValueUk: 'Factur-X v1.0 (PDF/A-3 + XML CII) / Profil EN 16931 (Comfort) / Facture FR-2026-5541',
    field1ValuePl: 'Factur-X v1.0 (PDF/A-3 + XML CII) / Profil EN 16931 / Faktura FR-2026-5541',
    field1ValueEn: 'Factur-X v1.0 (PDF/A-3 + XML CII) / Profile EN 16931 (Comfort) / Invoice FR-2026-5541',
    field1Value: 'Factur-X v1.0 (PDF/A-3 + XML CII) / Profil EN 16931 (Comfort) / Facture FR-2026-5541',
    field2LabelUk: 'Ідентифікатори SIREN / SIRET та TVA Intra',
    field2ValueUk: 'Fournisseur: Paris Conseil SAS (SIRET: 81234567800019 / TVA: FR12812345678) ↔ Client: Lyon Industrie SAS (SIRET: 98765432100025)',
    field2ValuePl: 'Dostawca: Paris Conseil SAS (SIRET: 81234567800019 / TVA: FR12812345678) ↔ Nabywca: Lyon Industrie SAS (SIRET: 98765432100025)',
    field2ValueEn: 'Supplier: Paris Conseil SAS (SIRET: 81234567800019 / VAT: FR12812345678) ↔ Buyer: Lyon Industrie SAS (SIRET: 98765432100025)',
    field2Value: 'Fournisseur: Paris Conseil SAS (SIRET: 81234567800019 / TVA: FR12812345678) ↔ Client: Lyon Industrie SAS (SIRET: 98765432100025)',
    field3LabelUk: 'Маршрутизація порталу Chorus Pro / Код сервісу',
    field3ValueUk: 'Code Service Chorus Pro: DAF-FINANCES / Engagement Juridique: EJ-2026-89012',
    field3ValuePl: 'Kod Serwisu Chorus Pro: DAF-FINANCES / Identyfikator umowy: EJ-2026-89012',
    field3ValueEn: 'Chorus Pro Service Code: DAF-FINANCES / Legal Commitment ID: EJ-2026-89012',
    field3Value: 'Code Service Chorus Pro: DAF-FINANCES / Engagement Juridique: EJ-2026-89012',
    field4LabelUk: 'База та ставки французького ПДВ (TVA)',
    field4ValueUk: 'Montant Total HT: €75 000.00 / TVA 20%: €15 000.00 / Montant Total TTC: €90 000.00 EUR',
    field4ValuePl: 'Wartość Netto (HT): 75 000.00 € / TVA 20%: 15 000.00 € / Wartość Brutto (TTC): 90 000.00 € EUR',
    field4ValueEn: 'Total Net (HT): €75,000.00 / French VAT 20%: €15,000.00 / Total Gross (TTC): €90,000.00 EUR',
    field4Value: 'Montant Total HT: €75 000.00 / TVA 20%: €15 000.00 / Montant Total TTC: €90 000.00 EUR',
    field5LabelUk: 'Валідація PDF/A-3 та відповідність реформі B2B',
    field5ValueUk: 'Стандарт PDF/A-3b підтверджено / Вбудований файл factur-x.xml синхронізований на 100%',
    field5ValuePl: 'Standard PDF/A-3b potwierdzony / Plik factur-x.xml zsynchronizowany w 100%',
    field5ValueEn: 'PDF/A-3b Conformance Verified / Embedded factur-x.xml fully synchronized',
    field5Value: 'Стандарт PDF/A-3b підтверджено / Вбудований файл factur-x.xml синхронізований на 100%',
    field6LabelUk: 'Формування розрахунку до декларації TVA CA3',
    field6ValueUk: 'Ligne 01 (Opérations imposables Taux normal 20%: €75 000.00) / Ligne 08 (TVA brute: €15 000.00)',
    field6ValuePl: 'Pozycja 01 (Operacje opodatkowane 20%: 75 000.00 €) / Pozycja 08 (Podatek należny: 15 000.00 €)',
    field6ValueEn: 'Line 01 (Taxable Operations 20%: €75,000.00) / Line 08 (Gross Output TVA: €15,000.00)',
    field6Value: 'Ligne 01 (Opérations imposables Taux normal 20%: €75 000.00) / Ligne 08 (TVA brute: €15 000.00)',
    notesTextUk: 'Здійснити аудит французького електронного гібридного рахунку Factur-X (EN 16931) та відповідності порталу державних закупівель Chorus Pro. Перевірити номери SIREN/SIRET у реєстрі INSEE, коректність розрахунку французького ПДВ TVA (20%) та підготувати підсумкову відомість до податкової декларації TVA CA3 у Word (.docx) та Excel (.xlsx).',
    notesTextPl: 'Przeprowadzić audyt francuskiej faktury hybrydowej Factur-X (EN 16931) oraz portalu Chorus Pro. Sprawdzić numery SIREN/SIRET w rejestrze INSEE, prawidłowość naliczenia podatku TVA 20% oraz przygotować raport do deklaracji TVA CA3 w Word (.docx) i Excel (.xlsx).',
    notesTextEn: 'Execute compliance audit of French hybrid Factur-X (EN 16931) e-invoice and Chorus Pro public procurement portal. Verify SIREN/SIRET registration via INSEE, check 20% French TVA calculation, and prepare TVA CA3 tax return schedule in Word (.docx) and Excel (.xlsx).'
  },
  'fop-tax-declarator': {
    field1LabelUk: 'Звітний Період & Група ФОП',
    field1LabelPl: 'Okres sprawozdawczy & Grupa JDG (FOP)',
    field1LabelEn: 'Tax Reporting Period & FOP Group',
    field1ValueUk: 'І Квартал 2026 року / ФОП 3 група (ставка 5% від доходу)',
    field1ValuePl: 'I Kwartał 2026 r. / FOP Grupa 3 (stawka 5% od przychodu)',
    field1ValueEn: 'Q1 2026 / FOP Group 3 (5% Simplified Tax Rate)',
    field1Value: 'І Квартал 2026 року / ФОП 3 група (ставка 5% від доходу)',

    field2LabelUk: 'Отриманий Валовий Дохід (грн) & Ліміт',
    field2LabelPl: 'Otrzymany przychód brutto (UAH) & Limit',
    field2LabelEn: 'Gross Received Income (UAH) & Annual Limit',
    field2ValueUk: '485 000.00 грн (Річний ліміт 3 групи: 10 091 049 грн у 2026 р. / 9 336 000 грн у 2024–2025 рр.)',
    field2ValuePl: '485 000.00 UAH (Roczny limit Grupy 3: 10 091 049 UAH w 2026 r. / 9 336 000 UAH w 2024–2025)',
    field2ValueEn: '485,000.00 UAH (Group 3 Annual Limit: 10,091,049 UAH in 2026 / 9,336,000 UAH in 2024–2025)',
    field2Value: '485 000.00 грн (Річний ліміт 3 групи: 10 091 049 грн у 2026 р. / 9 336 000 грн у 2024–2025 рр.)',

    field3LabelUk: 'Розрахований Єдиний Податок (грн)',
    field3LabelPl: 'Naliczony ryczałt / podatek jednolity (UAH)',
    field3LabelEn: 'Calculated Single Tax (UAH)',
    field3ValueUk: '24 250.00 грн (5% від 485 000.00 грн)',
    field3ValuePl: '24 250.00 UAH (5% od 485 000.00 UAH)',
    field3ValueEn: '24,250.00 UAH (5% of 485,000.00 UAH)',
    field3Value: '24 250.00 грн (5% від 485 000.00 грн)',

    field4LabelUk: 'Військовий Збір ФОП (Закон № 4015-IX)',
    field4LabelPl: 'Składka wojskowa JDG (Ustawa nr 4015-IX)',
    field4LabelEn: 'Military Tax for FOP (Law No. 4015-IX)',
    field4ValueUk: '4 850.00 грн (1% від доходу 485 000 грн за Законом № 4015-IX)',
    field4ValuePl: '4 850.00 UAH (1% od przychodu 485 000 UAH wg Ustawy nr 4015-IX)',
    field4ValueEn: '4,850.00 UAH (1% of income 485,000 UAH per Law No. 4015-IX)',
    field4Value: '4 850.00 грн (1% від доходу 485 000 грн за Законом № 4015-IX)',

    field5LabelUk: 'ЄСВ за себе (22% від мінзарплати: 8 647 грн у 2026 р.)',
    field5LabelPl: 'ZUS/ESV za siebie (22% od płacy min.: 8 647 UAH w 2026 r.)',
    field5LabelEn: 'Single Social Contribution (22% of min. wage: 8,647 UAH in 2026)',
    field5ValueUk: '5 707.02 грн за I кв. 2026 (1 902.34 грн/міс * 3 міс. / 5 280 грн у 2024–2025 рр.)',
    field5ValuePl: '5 707.02 UAH za I kw. 2026 (1 902.34 UAH/mies. * 3 mies. / 5 280 UAH w 2024–2025)',
    field5ValueEn: '5,707.02 UAH for Q1 2026 (1,902.34 UAH/mo * 3 mos / 5,280 UAH in 2024–2025)',
    field5Value: '5 707.02 грн за I кв. 2026 (1 902.34 грн/міс * 3 міс. / 5 280 грн у 2024–2025 рр.)',

    field6LabelUk: 'Кількість найманих працівників & Книга обліку',
    field6LabelPl: 'Liczba zatrudnionych pracowników & Księga przychodów',
    field6LabelEn: 'Number of Hired Employees & Revenue Ledger',
    field6ValueUk: '0 найманих працівників, Книга обліку доходів в електронному кабінеті ДПС ведеться',
    field6ValuePl: '0 pracowników zatrudnionych, elektroniczna ewidencja przychodów w DPS',
    field6ValueEn: '0 hired employees, revenue ledger maintained in DPS Electronic Cabinet',
    field6Value: '0 найманих працівників, Книга обліку доходів в електронному кабінеті ДПС ведеться',

    notesTextUk: 'Провести камеральний аудит господарської діяльності ФОП 3 групи (ІТ-послуги та консалтинг) за І квартал 2026 року. Розрахувати Єдиний податок 5% (24 250.00 грн), Військовий збір 1% згідно із Законом № 4015-IX (4 850.00 грн) та мінімальний страховий внесок з ЄСВ за квартал (5 707.02 грн, 1 902.34 грн/міс при МЗП 8 647 грн). Сформувати офіційний проєкт Податкової декларації платника єдиного податку ФОП (форма F0103308) з Додатком 1 по ЄСВ за ДСТУ 4163:2020 та сформувати платіжні інструкції з реквізитами IBAN.',
    notesTextPl: 'Przeprowadzić audyt rozliczenia jednoosobowej działalności gospodarczej (FOP Grupa 3, IT) za I kwartał 2026 r. Naliczyć podatek jednolity 5% (24 250 UAH), składkę wojskową 1% (4 850 UAH) oraz ZUS/ESV (5 707.02 UAH, 1 902.34 UAH/mies. przy płacy min. 8 647 UAH). Wygenerować projekt deklaracji podatkowej F0103308 z załącznikiem ZUS oraz wykaz rachunków do wpłaty w Word (.docx).',
    notesTextEn: 'Execute tax audit for Sole Proprietor (FOP Group 3, IT consulting) for Q1 2026. Calculate 5% Single Tax (24,250 UAH), 1% Military Tax per Law No. 4015-IX (4,850 UAH), and mandatory quarterly ESV contribution (5,707.02 UAH, 1,902.34 UAH/mo at min wage 8,647 UAH). Generate official FOP Tax Return draft (Form F0103308) with ESV Schedule 1 under DSTU 4163:2020 and bank payment orders with IBANs.'
  },
  'corporate-income-tax-declarator': {
    field1LabelUk: 'Звітний Період & Система оподаткування',
    field1LabelPl: 'Okres rozliczeniowy & Forma opodatkowania',
    field1LabelEn: 'Reporting Period & Corporate Tax System',
    field1ValueUk: '2026 рік (Річна звітність) / ТОВ Загальна система (ставка 18%)',
    field1ValuePl: '2026 r. (Roczna deklaracja) / Sp. z o.o. zasady ogólne CIT 18%',
    field1ValueEn: 'FY 2026 (Annual Return) / General System LLC (18% CIT rate)',
    field1Value: '2026 рік (Річна звітність) / ТОВ Загальна система (ставка 18%)',

    field2LabelUk: 'Дохід від усіх видів діяльності (ряд. 01, грн)',
    field2LabelPl: 'Przychód z działalności gospodarczej (poz. 01, UAH)',
    field2LabelEn: 'Total Revenue from All Activities (Line 01, UAH)',
    field2ValueUk: '54 200 000.00 грн (Перевищує поріг 40 млн грн — обов\'язкові податкові різниці)',
    field2ValuePl: '54 200 000.00 UAH (Powyżej 40 mln UAH — obowiązkowe różnice podatkowe)',
    field2ValueEn: '54,200,000.00 UAH (Exceeds 40M UAH threshold — mandatory tax adjustments)',
    field2Value: '54 200 000.00 грн (Перевищує поріг 40 млн грн — обов\'язкові податкові різниці)',

    field3LabelUk: 'Фінрезультат до оподаткування (ряд. 02, грн)',
    field3LabelPl: 'Wynik finansowy brutto (poz. 02, UAH)',
    field3LabelEn: 'Financial Result Before Tax (Line 02, UAH)',
    field3ValueUk: '6 400 000.00 грн (Прибуток за Звітом про фінансові результати ф. № 2)',
    field3ValuePl: '6 400 000.00 UAH (Zysk brutto wg sprawozdania finansowego RZiS)',
    field3ValueEn: '6,400,000.00 UAH (Pre-tax profit per P&L Statement Form No. 2)',
    field3Value: '6 400 000.00 грн (Прибуток за Звітом про фінансові результати ф. № 2)',

    field4LabelUk: 'Податкові різниці Додаток РІ (+/-)',
    field4LabelPl: 'Różnice podatkowe Załącznik RI (+/-)',
    field4LabelEn: 'Tax Differences Schedule RI (+/- adjustments)',
    field4ValueUk: '+420 000.00 грн (Амортизаційні різниці ст. 138: +180 000 грн, Резерв сумнівних боргів: +240 000 грн)',
    field4ValuePl: '+420 000.00 UAH (Amortyzacja art. 138: +180 000 UAH, Rezerwy na długi: +240 000 UAH)',
    field4ValueEn: '+420,000.00 UAH (Depreciation art. 138: +180,000 UAH, Bad debt reserve: +240,000 UAH)',
    field4Value: '+420 000.00 грн (Амортизаційні різниці ст. 138: +180 000 грн, Резерв сумнівних боргів: +240 000 грн)',

    field5LabelUk: 'Податкова амортизація ОЗ (Додаток АМ, грн)',
    field5LabelPl: 'Amortyzacja podatkowa ŚT (Załącznik AM, UAH)',
    field5LabelEn: 'Tax Depreciation of Fixed Assets (Schedule AM, UAH)',
    field5ValueUk: '1 250 000.00 грн (Група 3 «Будівлі»: 450 тис. грн, Група 4 «Машини та обладнання»: 800 тис. грн)',
    field5ValuePl: '1 250 000.00 UAH (Grupa 3 «Budynki»: 450 tys. UAH, Grupa 4 «Maszyny»: 800 tys. UAH)',
    field5ValueEn: '1,250,000.00 UAH (Group 3 Buildings: 450k UAH, Group 4 Equipment: 800k UAH)',
    field5Value: '1 250 000.00 грн (Група 3 «Будівлі»: 450 тис. грн, Група 4 «Машини та обладнання»: 800 тис. грн)',

    field6LabelUk: 'Авансові внески з дивідендів / Залік (грн)',
    field6LabelPl: 'Zaliczki na CIT z dywidend / Odliczenia (UAH)',
    field6LabelEn: 'Advance Dividend Tax Offsets / Deductions (UAH)',
    field6ValueUk: '150 000.00 грн (Сплачений авансовий внесок з дивідендів, що підлягає зарахуванню за ряд. 16.1 Декларації)',
    field6ValuePl: '150 000.00 UAH (Zaliczka od dywidend do odliczenia w poz. 16.1)',
    field6ValueEn: '150,000.00 UAH (Advance dividend corporate tax offset per Line 16.1)',
    field6Value: '150 000.00 грн (Сплачений авансовий внесок з дивідендів, що підлягає зарахуванню за ряд. 16.1 Декларації)',

    notesTextUk: 'Скласти офіційний проєкт Податкової декларації з податку на прибуток підприємств за формою J0100125 за 2026 рік. Провести розрахунок об\'єкта оподаткування (Рядок 04 = Рядок 02 + Рядок 03 РІ = 6 400 000 + 420 000 = 6 820 000 грн). Нарахувати податок на прибуток 18% (1 227 600 грн), провести залік авансового внеску 150 000 грн та визначити суму податку до сплати в бюджет (1 077 600 грн). Сформувати Додатки РІ, АМ, розрахунок платіжних реквізитів та податковий звіт у Word (.docx за ДСТУ 4163:2020).',
    notesTextPl: 'Sporządzić oficjalny projekt deklaracji CIT (formularz J0100125) za 2026 rok. Dokonać kalkulacji podstawy opodatkowania (poz. 04 = 6 820 000 UAH), podatku dochodowego CIT 18% (1 227 600 UAH), odliczenia zaliczki dywidendowej i określenia kwoty do zapłaty (1 077 600 UAH). Przygotować załączniki RI i AM w Word (.docx).',
    notesTextEn: 'Prepare official Corporate Income Tax Declaration draft (Form J0100125) for FY 2026. Calculate taxable base (Line 04 = Line 02 + Line 03 RI = 6,820,000 UAH), 18% CIT liability (1,227,600 UAH), deduct advance dividend tax offset of 150,000 UAH to determine final net payable CIT of 1,077,600 UAH. Generate Schedules RI, AM and official DSTU 4163:2020 report in Word (.docx).'
  },
  'vat-tax-declarator': {
    field1LabelUk: 'Звітний Період & Реєстраційний Ліміт СЕА',
    field1LabelPl: 'Okres sprawozdawczy & Limit rejestracyjny VAT',
    field1LabelEn: 'Reporting Period & SEA VAT Registration Limit',
    field1ValueUk: 'Березень 2026 року / Реєстраційний ліміт в СЕА ПДВ (ΣНакл): 1 850 000.00 грн',
    field1ValuePl: 'Marzec 2026 r. / Dostępny limit rejestracyjny w SEA VAT: 1 850 000.00 UAH',
    field1ValueEn: 'March 2026 / Available SEA VAT Registration Limit (ΣNakl): 1,850,000.00 UAH',
    field1Value: 'Березень 2026 року / Реєстраційний ліміт в СЕА ПДВ (ΣНакл): 1 850 000.00 грн',

    field2LabelUk: 'Податкові Зобов\'язання ПДВ 20% (ряд. 1.1, грн)',
    field2LabelPl: 'Należny podatek VAT 20% (poz. 1.1, UAH)',
    field2LabelEn: 'Output VAT Liabilities 20% (Line 1.1, UAH)',
    field2ValueUk: '850 000.00 грн (База оподаткування 20%: 4 250 000.00 грн)',
    field2ValuePl: '850 000.00 UAH (Podstawa opodatkowania 20%: 4 250 000.00 UAH)',
    field2ValueEn: '850,000.00 UAH (Taxable turnover at 20%: 4,250,000.00 UAH)',
    field2Value: '850 000.00 грн (База оподаткування 20%: 4 250 000.00 грн)',

    field3LabelUk: 'Податковий Кредит з ПДВ (ряд. 10.1, грн)',
    field3LabelPl: 'Naliczony podatek VAT (poz. 10.1, UAH)',
    field3LabelEn: 'Input Tax Credit (Line 10.1, UAH)',
    field3ValueUk: '620 000.00 грн (Придбання з ПДВ 20%: 3 100 000.00 грн за зареєстрованими ПН постачальників)',
    field3ValuePl: '620 000.00 UAH (Nabycia z VAT 20%: 3 100 000.00 UAH z zarejestrowanych faktur)',
    field3ValueEn: '620,000.00 UAH (Purchases with 20% VAT: 3,100,000.00 UAH from registered invoices)',
    field3Value: '620 000.00 грн (Придбання з ПДВ 20%: 3 100 000.00 грн за зареєстрованими ПН постачальників)',

    field4LabelUk: 'Коригування ПК/ПЗ (Додаток Д1, грн)',
    field4LabelPl: 'Korekty VAT Załącznik D1 (UAH)',
    field4LabelEn: 'VAT Adjustments Schedule D1 (UAH)',
    field4ValueUk: '+35 000.00 грн (Збільшення ПЗ за розрахунками коригування РК покупцям на повернення)',
    field4ValuePl: '+35 000.00 UAH (Korekty zwiększające podatek należny z tytułu zwrotów)',
    field4ValueEn: '+35,000.00 UAH (Upward output VAT adjustments from customer return notes)',
    field4Value: '+35 000.00 грн (Збільшення ПЗ за розрахунками коригування РК покупцям на повернення)',

    field5LabelUk: 'Заблоковані ПН постачальників у СМКОР (грн)',
    field5LabelPl: 'Zablokowane faktury dostawców w SMkor (UAH)',
    field5LabelEn: 'Suspended Supplier VAT Invoices in SMkor (UAH)',
    field5ValueUk: '48 000.00 грн (2 ПН постачальників зупинено за п. 1 критеріїв — виключено з ПК до розблокування)',
    field5ValuePl: '48 000.00 UAH (2 faktury wstrzymane wg kryteriów — wyłączone z odliczenia)',
    field5ValueEn: '48,000.00 UAH (2 supplier invoices halted under p. 1 criteria — excluded from credit)',
    field5Value: '48 000.00 грн (2 ПН постачальників зупинено за п. 1 критеріїв — виключено з ПК до розблокування)',

    field6LabelUk: 'Обсяг операцій за ставкою 0% (Експорт, грн)',
    field6LabelPl: 'Eksport towarów ze stawką 0% (UAH)',
    field6LabelEn: 'Zero-Rated Export Transactions (Line 2, UAH)',
    field6ValueUk: '1 200 000.00 грн (Експорт промислового обладнання за митними деклараціями МД-2)',
    field6ValuePl: '1 200 000.00 UAH (Eksport towarów wg zgłoszeń celnych SAD/MD-2)',
    field6ValueEn: '1,200,000.00 UAH (Industrial equipment exports under Customs Declarations MD-2)',
    field6Value: '1 200 000.00 грн (Експорт промислового обладнання за митними деклараціями МД-2)',

    notesTextUk: 'Скласти офіційний проєкт Податкової декларації з ПДВ за формою J0200125 за березень 2026 року. Розрахувати чисте податкове зобов\'язання до сплати в бюджет (Рядок 18 = 850 000 + 35 000 - 620 000 = 265 000.00 грн). Перевірити наявність коштів на електронному рахунку СЕА ПДВ, сформувати Додаток Д1 щодо коригувань, виділити тимчасово виключений ПК за заблокованими накладними (48 000 грн) та підготувати звіт у Word (.docx за ДСТУ 4163:2020).',
    notesTextPl: 'Sporządzić oficjalny projekt deklaracji VAT (formularz J0200125) za marzec 2026 r. Naliczyć kwotę netto do zapłaty (poz. 18 = 265 000.00 UAH). Zweryfikować stan konta w SEA VAT, sporządzić załącznik D1, zidentyfikować ryzyko zablokowanych faktur (48 000 UAH) oraz wygenerować raport w Word (.docx).',
    notesTextEn: 'Prepare official VAT Return draft (Form J0200125) for March 2026. Calculate net VAT payable to state budget (Line 18 = 850,000 + 35,000 - 620,000 = 265,000.00 UAH). Verify funds on SEA electronic tax account, prepare Schedule D1, identify suspended supplier credits (48,000 UAH) and compile DSTU 4163:2020 report in Word (.docx).'
  },
  'payroll-tax-declarator': {
    field1LabelUk: 'Звітний Період & Форма Звітності',
    field1LabelPl: 'Okres rozliczeniowy & Formularz',
    field1LabelEn: 'Reporting Period & Unified Form',
    field1ValueUk: 'І Квартал 2026 року / Податковий розрахунок сум доходу (форма J0500109)',
    field1ValuePl: 'I Kwartał 2026 r. / Zunifikowany raport płacowy (formularz J0500109)',
    field1ValueEn: 'Q1 2026 / Unified Payroll Tax Calculation (Form J0500109)',
    field1Value: 'І Квартал 2026 року / Податковий розрахунок сум доходу (форма J0500109)',

    field2LabelUk: 'Фонд Оплати Праці / Нарахована ЗП (грн)',
    field2LabelPl: 'Fundusz płac / Wynagrodzenia brutto (UAH)',
    field2LabelEn: 'Total Gross Payroll Fund (Line 1, UAH)',
    field2ValueUk: '840 000.00 грн (Помісячно: січень 280 тис. грн, лютий 280 тис. грн, березень 280 тис. грн)',
    field2ValuePl: '840 000.00 UAH (Miesięcznie: styczeń 280 tys., luty 280 tys., marzec 280 tys.)',
    field2ValueEn: '840,000.00 UAH (Monthly: Jan 280k UAH, Feb 280k UAH, Mar 280k UAH)',
    field2Value: '840 000.00 грн (Помісячно: січень 280 тис. грн, лютий 280 тис. грн, березень 280 тис. грн)',

    field3LabelUk: 'Штатна чисельність працівників (осіб)',
    field3LabelPl: 'Liczba zatrudnionych na umowę o pracę',
    field3LabelEn: 'Headcount of Staff Employees',
    field3ValueUk: '20 штатних працівників (всі на умовах повного робочого дня, 1 особа з інвалідністю)',
    field3ValuePl: '20 pracowników etatowych (w tym 1 osoba z niepełnosprawnością)',
    field3ValueEn: '20 full-time staff employees (including 1 person with disability)',
    field3Value: '20 штатних працівників (всі на умовах повного робочого дня, 1 особа з інвалідністю)',

    field4LabelUk: 'Нарахований ЄСВ 22% та 8.41% (Додаток Д1)',
    field4LabelPl: 'Naliczony ZUS/ESV 22% i 8.41% (Załącznik D1)',
    field4LabelEn: 'Accrued ESV at 22% & 8.41% (Schedule D1)',
    field4ValueUk: '181 440.00 грн (19 працівників по 22%: 175 560 грн, 1 особа з інвалідністю по 8.41%: 5 880 грн)',
    field4ValuePl: '181 440.00 UAH (19 osób po 22%: 175 560 UAH, 1 osoba 8.41%: 5 880 UAH)',
    field4ValueEn: '181,440.00 UAH (19 staff at 22%: 175,560 UAH, 1 disabled at 8.41%: 5,880 UAH)',
    field4Value: '181 440.00 грн (19 працівників по 22%: 175 560 грн, 1 особа з інвалідністю по 8.41%: 5 880 грн)',

    field5LabelUk: 'Утримано ПДФО 18% (Додаток 4ДФ, грн)',
    field5LabelPl: 'Pobrany PIT/PDKhO 18% (Załącznik 4DF)',
    field5LabelEn: 'Withheld Personal Income Tax 18% (Schedule 4DF)',
    field5ValueUk: '151 200.00 грн (18% від нарахованого доходу 840 000 грн за ознакою «101»)',
    field5ValuePl: '151 200.00 UAH (18% od przychodu 840 000 UAH kod «101»)',
    field5ValueEn: '151,200.00 UAH (18% of gross wages 840,000 UAH under code 101)',
    field5Value: '151 200.00 грн (18% від нарахованого доходу 840 000 грн за ознакою «101»)',

    field6LabelUk: 'Військовий Збір 5% (Закон № 4015-IX, грн)',
    field6LabelPl: 'Składka wojskowa 5% (Ustawa nr 4015-IX)',
    field6LabelEn: 'Military Tax at 5% (Law No. 4015-IX, UAH)',
    field6ValueUk: '42 000.00 грн (5% від доходу 840 000 грн за новою ставкою Закону № 4015-IX)',
    field6ValuePl: '42 000.00 UAH (5% od przychodu 840 000 UAH wg nowej stawki)',
    field6ValueEn: '42,000.00 UAH (5% of wages 840,000 UAH per Law No. 4015-IX)',
    field6Value: '42 000.00 грн (5% від доходу 840 000 грн за новою ставкою Закону № 4015-IX)',

    notesTextUk: 'Скласти офіційний Податковий розрахунок сум доходу та нарахованого ЄСВ (форма J0500109) за І квартал 2026 року з Додатком 1 (ЄСВ) та Додатком 4ДФ (ПДФО 18% та Військовий збір за новою ставкою 5% згідно із Законом України № 4015-IX). Розрахувати чисту зарплату до виплати працівникам (840 000 - 151 200 - 42 000 = 646 800 грн) та підготувати платіжні інструкції для Казначейства за ДСТУ 4163:2020.',
    notesTextPl: 'Sporządzić oficjalny raport płacowy J0500109 za I kwartał 2026 r. z załącznikami D1 (ZUS) oraz 4DF (PIT 18% i nowa składka wojskowa 5% wg Ustawy nr 4015-IX). Obliczyć kwotę netto do wypłaty (646 800 UAH) oraz wygenerować zlecenia płatnicze w Word (.docx).',
    notesTextEn: 'Prepare official Unified Payroll Tax Return (Form J0500109) for Q1 2026 with Schedule D1 (ESV) and Schedule 4DF (18% PIT and new 5% Military Tax rate under Law No. 4015-IX). Calculate net wages payable to employees (646,800 UAH) and generate treasury bank orders under DSTU 4163:2020 in Word (.docx).'
  },
  'resource-property-tax-declarator': {
    field1LabelUk: 'Звітний Період & Об\'єкт оподаткування',
    field1LabelPl: 'Okres sprawozdawczy & Przedmiot opodatkowania',
    field1LabelEn: 'Reporting Period & Taxable Property Object',
    field1ValueUk: '2026 рік (Річна звітність) / Земельна ділянка комерційного призначення',
    field1ValuePl: '2026 r. (Roczna deklaracja) / Grunt komercyjny',
    field1ValueEn: 'FY 2026 (Annual Return) / Commercial Land Plot',
    field1Value: '2026 рік (Річна звітність) / Земельна ділянка комерційного призначення',

    field2LabelUk: 'Кадастровий номер & Площа ділянки',
    field2LabelPl: 'Numer ewidencyjny działki & Powierzchnia',
    field2LabelEn: 'Cadastral Number & Land Area',
    field2ValueUk: '3222485800:02:001:0048 / Площа: 2.4500 га (м. Бровари, Київська обл.)',
    field2ValuePl: '3222485800:02:001:0048 / Powierzchnia: 2.4500 ha',
    field2ValueEn: '3222485800:02:001:0048 / Area: 2.4500 ha (Kyiv Region)',
    field2Value: '3222485800:02:001:0048 / Площа: 2.4500 га (м. Бровари, Київська обл.)',

    field3LabelUk: 'Нормативна грошова оцінка (НГО, грн)',
    field3LabelPl: 'Wartość szacunkowa gruntu (NGO, UAH)',
    field3LabelEn: 'Monetary Valuation of Land (NGO, UAH)',
    field3ValueUk: '18 400 000.00 грн (З урахуванням коефіцієнта індексації НГО за 2025 рік)',
    field3ValuePl: '18 400 000.00 UAH (Z uwzględnieniem wskaźnika waloryzacji)',
    field3ValueEn: '18,400,000.00 UAH (Indexed according to State Land Cadastre)',
    field3Value: '18 400 000.00 грн (З урахуванням коефіцієнта індексації НГО за 2025 рік)',

    field4LabelUk: 'Ставка податку (% згідно з рішенням міськради)',
    field4LabelPl: 'Stawka podatku od nieruchomości / gruntu (%)',
    field4LabelEn: 'Property / Land Tax Rate (% by City Council)',
    field4ValueUk: '1.50% від НГО (Рішення Броварської міської ради для земель промисловості)',
    field4ValuePl: '1.50% od wartości gruntu (Uchwała rady miejskiej)',
    field4ValueEn: '1.50% of valuation (City Council resolution for industrial land)',
    field4Value: '1.50% від НГО (Рішення Броварської міської ради для земель промисловості)',

    field5LabelUk: 'Річна сума податку & Щомісячний платіж',
    field5LabelPl: 'Roczna kwota podatku & Płatność miesięczna',
    field5LabelEn: 'Annual Tax Amount & Monthly Installment',
    field5ValueUk: 'Річна сума: 276 000.00 грн / Щомісячний платіж (1/12): 23 000.00 грн',
    field5ValuePl: 'Rocznie: 276 000.00 UAH / Miesięcznie (1/12): 23 000.00 UAH',
    field5ValueEn: 'Annual tax: 276,000.00 UAH / Monthly (1/12): 23,000.00 UAH',
    field5Value: 'Річна сума: 276 000.00 грн / Щомісячний платіж (1/12): 23 000.00 грн',

    field6LabelUk: 'Наявність пільг або об\'єктів нерухомості',
    field6LabelPl: 'Ulgi podatkowe lub obiekty budowlane',
    field6LabelEn: 'Tax Exemptions or Building Structures',
    field6ValueUk: 'Пільги відсутні, на ділянці розташовано виробничо-складський комплекс площею 3 200 кв.м',
    field6ValuePl: 'Brak ulg, na działce znajduje się magazyn o pow. 3 200 m²',
    field6ValueEn: 'No exemptions, property features a 3,200 sq.m industrial warehouse',
    field6Value: 'Пільги відсутні, на ділянці розташовано виробничо-складський комплекс площею 3 200 кв.м',

    notesTextUk: 'Скласти офіційну Податкову декларацію з плати за землю (земельний податок) за формою J0602008 на 2026 рік. Розрахувати помісячні податкові зобов\'язання (по 23 000 грн до 30 числа щомісяця), сформувати платіжні інструкції з КБКБ 18010500 та підготувати звіт у Word (.docx за ДСТУ 4163:2020).',
    notesTextPl: 'Sporządzić deklarację podatku od nieruchomości / podatku gruntowego J0602008 na rok 2026. Obliczyć miesięczne raty (23 000 UAH) oraz wygenerować raport w Word (.docx).',
    notesTextEn: 'Prepare official Land Property Tax Declaration (Form J0602008) for FY 2026. Calculate monthly tax installments (23,000 UAH due by the 30th of each month) and compile DSTU 4163:2020 report in Word (.docx).'
  },
  'excise-tax-declarator': {
    field1LabelUk: 'Звітний Період & Ліцензія',
    field1LabelPl: 'Okres sprawozdawczy & Licencja akcyzowa',
    field1LabelEn: 'Reporting Period & Excise License',
    field1ValueUk: 'Березень 2026 року / Ліцензія на роздрібну торгівлю пальним та алкоголем № 26038492',
    field1ValuePl: 'Marzec 2026 r. / Licencja na handel detaliczny paliwami i alkoholem',
    field1ValueEn: 'March 2026 / Retail fuel and alcohol license No. 26038492',
    field1Value: 'Березень 2026 року / Ліцензія на роздрібну торгівлю пальним та алкоголем № 26038492',

    field2LabelUk: 'Вид підакцизного товару & Код УКТ ЗЕД',
    field2LabelPl: 'Rodzaj towaru akcyzowego & Kod CN/UKT ZED',
    field2LabelEn: 'Excise Product Type & HS/UKT ZED Code',
    field2ValueUk: 'Бензин автомобільний А-95 (Код УКТ ЗЕД 2710 12 45 00) та Алкогольні напої (Код 2208)',
    field2ValuePl: 'Benzyna A-95 (Kod CN 2710 12 45 00) oraz Napoje alkoholowe',
    field2ValueEn: 'Motor Gasoline A-95 (HS Code 2710 12 45 00) & Spirits (HS 2208)',
    field2Value: 'Бензин автомобільний А-95 (Код УКТ ЗЕД 2710 12 45 00) та Алкогольні напої (Код 2208)',

    field3LabelUk: 'Обсяг реалізації підакцизних товарів (грн)',
    field3LabelPl: 'Wartość sprzedaży wyrobów akcyzowych (UAH)',
    field3LabelEn: 'Gross Retail Excise Sales Turnover (UAH)',
    field3ValueUk: '2 850 000.00 грн (Пальне: 2 100 000 грн, Алкоголь/Тютюн: 750 000 грн)',
    field3ValuePl: '2 850 000.00 UAH (Paliwa: 2 100 000 UAH, Alkohol/Tytoń: 750 000 UAH)',
    field3ValueEn: '2,850,000.00 UAH (Fuel: 2,100,000 UAH, Alcohol/Tobacco: 750,000 UAH)',
    field3Value: '2 850 000.00 грн (Пальне: 2 100 000 грн, Алкоголь/Тютюн: 750 000 грн)',

    field4LabelUk: 'Обсяг у літрах / кілограмах за СЕАРП',
    field4LabelPl: 'Wolumen w litrach w rejestrze SEARP',
    field4LabelEn: 'Volume in Liters / Kg per SEARP Register',
    field4ValueUk: '42 500 літрів пального за зареєстрованими акцизними накладними в системі СЕАРП',
    field4ValuePl: '42 500 litrów paliwa wg zarejestrowanych faktur akcyzowych SEARP',
    field4ValueEn: '42,500 liters of motor fuel registered in SEARP system',
    field4Value: '42 500 літрів пального за зареєстрованими акцизними накладними в системі СЕАРП',

    field5LabelUk: 'Ставка акцизного податку',
    field5LabelPl: 'Stawka podatku akcyzowego',
    field5LabelEn: 'Applicable Excise Tax Rate',
    field5ValueUk: '5% роздрібний акциз (для алкоголю/тютюну) та специфічна ставка в EUR за 1000 л пального',
    field5ValuePl: '5% akcyza detaliczna oraz stawka specyficzna w EUR/1000 l',
    field5ValueEn: '5% retail excise (alcohol/tobacco) and specific rate in EUR per 1,000 L of fuel',
    field5Value: '5% роздрібний акциз (для алкоголю/тютюну) та специфічна ставка в EUR за 1000 л пального',

    field6LabelUk: 'Залишок пального в резервуарах (л, ф. 20-ОПП)',
    field6LabelPl: 'Stan paliwa w zbiornikach (formularz 20-OPP)',
    field6LabelEn: 'Tank Fuel Balance (Form 20-OPP, Liters)',
    field6ValueUk: '18 200 літрів (Рівнеміри-лічильники та витратоміри опломбовані та зареєстровані в ДПС)',
    field6ValuePl: '18 200 litrów (Zalegalizowane przepływomierze)',
    field6ValueEn: '18,200 liters (Sealed and certified automated flowmeters)',
    field6Value: '18 200 літрів (Рівнеміри-лічильники та витратоміри опломбовані та зареєстровані в ДПС)',

    notesTextUk: 'Скласти офіційний проєкт Декларації акцизного податку (форма J0201416) за березень 2026 року з Додатками 1 (розрахунок суми акцизу з пального) та 6 (роздрібний акциз 5% з алкоголю). Перевірити відповідність залишків у СЕАРП та підготувати звіт у Word (.docx за ДСТУ 4163:2020).',
    notesTextPl: 'Sporządzić deklarację podatku akcyzowego J0201416 za marzec 2026 r. z załącznikami 1 i 6. Zweryfikować stany w systemie SEARP i wygenerować raport w Word (.docx).',
    notesTextEn: 'Prepare official Excise Tax Return (Form J0201416) for March 2026 with Schedule 1 (fuel excise calculation) and Schedule 6 (5% retail excise). Verify SEARP inventory balances and compile DSTU 4163:2020 report in Word (.docx).'
  },
  'special-tax-regimes-declarator': {
    field1LabelUk: 'Спеціальний режим & Статус резидента',
    field1LabelPl: 'Specjalny reżim podatkowy & Status rezydenta',
    field1LabelEn: 'Special Tax Regime & Resident Status',
    field1ValueUk: 'Резидент простору Дія.City (Оподаткування на особливих умовах — ПнВК 9%)',
    field1ValuePl: 'Rezydent Diia.City (Opodatkowanie na zasadach estońskiego CIT 9%)',
    field1ValueEn: 'Diia.City Resident (Special corporate regime — 9% Distributed Profit Tax)',
    field1Value: 'Резидент простору Дія.City (Оподаткування на особливих умовах — ПнВК 9%)',

    field2LabelUk: 'Звітний Період & Валовий дохід (грн)',
    field2LabelPl: 'Okres rozliczeniowy & Przychód brutto (UAH)',
    field2LabelEn: 'Reporting Period & Gross Revenue (UAH)',
    field2ValueUk: '2026 рік (Річна звітність) / Валовий дохід від ІТ-діяльності: 48 500 000.00 грн',
    field2ValuePl: '2026 r. / Przychód brutto z działalności IT: 48 500 000.00 UAH',
    field2ValueEn: 'FY 2026 / Gross IT software revenue: 48,500,000.00 UAH',
    field2Value: '2026 рік (Річна звітність) / Валовий дохід від ІТ-діяльності: 48 500 000.00 грн',

    field3LabelUk: 'Операції, що оподатковуються ПнВК 9%',
    field3LabelPl: 'Operacje podlegające opodatkowaniu CIT 9%',
    field3LabelEn: 'Operations Subject to 9% DPT (CIT)',
    field3ValueUk: 'Виплата дивідендів засновнику-нерезиденту: 3 000 000 грн (Сума податку 9%: 270 000 грн)',
    field3ValuePl: 'Wypłata dywidendy dla nierezydenta: 3 000 000 UAH (Podatek 9%: 270 000 UAH)',
    field3ValueEn: 'Dividend distribution to non-resident: 3,000,000 UAH (9% Tax: 270,000 UAH)',
    field3Value: 'Виплата дивідендів засновнику-нерезиденту: 3 000 000 грн (Сума податку 9%: 270 000 грн)',

    field4LabelUk: 'Кількість Гіг-спеціалістів & Середня винагорода',
    field4LabelPl: 'Liczba gig-specjalistów & Średnie wynagrodzenie',
    field4LabelEn: 'Gig Specialists Headcount & Average Fee',
    field4ValueUk: '24 гіг-спеціалісти, середня винагорода — еквівалент 1 850 EUR (норма: не менше 1200 EUR)',
    field4ValuePl: '24 gig-specjalistów, średnia płaca 1 850 EUR (norma min. 1200 EUR)',
    field4ValueEn: '24 gig specialists, average monthly remuneration 1,850 EUR (statutory min: 1,200 EUR)',
    field4Value: '24 гіг-спеціалісти, середня винагорода — еквівалент 1 850 EUR (норма: не менше 1200 EUR)',

    field5LabelUk: 'Оподаткування доходів гіг-спеціалістів (ПДФО 5%, ЄСВ)',
    field5LabelPl: 'Opodatkowanie gig-specjalistów (PIT 5%, ZUS)',
    field5LabelEn: 'Gig Workers Taxation (5% PIT & Min. ESV)',
    field5ValueUk: 'ПДФО 5% (112 500 грн/міс), Військовий збір 5%, ЄСВ 22% від мінзарплати 8 647 грн (1 902.34 грн/особу у 2026 р. / 1 760 грн у 2024–2025 рр.)',
    field5ValuePl: 'PIT 5%, składka wojskowa 5%, ZUS/ESV 22% od min. płacy 8 647 UAH (1 902.34 UAH/osobę w 2026 r. / 1 760 UAH w 2024–2025)',
    field5ValueEn: '5% PIT, 5% Military Tax, 22% ESV on min wage 8,647 UAH (1,902.34 UAH/person in 2026 / 1,760 UAH in 2024–2025)',
    field5Value: 'ПДФО 5% (112 500 грн/міс), Військовий збір 5%, ЄСВ 22% від мінзарплати 8 647 грн (1 902.34 грн/особу у 2026 р. / 1 760 грн у 2024–2025 рр.)',

    field6LabelUk: 'Додаток Дія (Додаток ДІЯ до Декларації з прибутку)',
    field6LabelPl: 'Załącznik DIIA do deklaracji CIT',
    field6LabelEn: 'Schedule DIIA to Corporate Tax Declaration',
    field6ValueUk: 'Заповнення рядків 01-15 Додатка ДІЯ щодо виплат нерезидентам та роялті',
    field6ValuePl: 'Wypełnienie pozycji 01-15 Załącznika DIIA',
    field6ValueEn: 'Filing of Lines 01-15 of Schedule DIIA for distributions & royalties',
    field6Value: 'Заповнення рядків 01-15 Додатка ДІЯ щодо виплат нерезидентам та роялті',

    notesTextUk: 'Скласти офіційний податковий звіт та проєкт Декларації з податку на прибуток резидента Дія.City на особливих умовах (ПнВК 9%) з Додатком ДІЯ за 2026 рік. Розрахувати податок з виплачених дивідендів (270 000 грн), перевірити критерії комплаєнсу (частка ІТ-доходу 98%, середня зарплата > 1200 EUR) та підготувати звіт у Word (.docx за ДСТУ 4163:2020).',
    notesTextPl: 'Sporządzić oficjalny raport i projekt deklaracji CIT dla rezydenta Diia.City (estoński CIT 9%) z Załącznikiem DIIA za 2026 r. Naliczyć podatek od dywidendy (270 000 UAH) i wygenerować dokument w Word (.docx).',
    notesTextEn: 'Prepare official tax calculation and Corporate Income Tax draft for Diia.City resident under 9% Distributed Profit Tax with Schedule DIIA for FY 2026. Calculate dividend withholding (270,000 UAH), verify compliance ratios and compile DSTU 4163:2020 report in Word (.docx).'
  },
  'local-fees-declarator': {
    field1LabelUk: 'Звітний Період & Вид збору',
    field1LabelPl: 'Okres rozliczeniowy & Rodzaj opłaty lokalnej',
    field1LabelEn: 'Reporting Period & Local Fee Type',
    field1ValueUk: 'І Квартал 2026 року / Туристичний Збір (ст. 268 ПКУ)',
    field1ValuePl: 'I Kwartał 2026 r. / Opłata turystyczna (art. 268 PKU)',
    field1ValueEn: 'Q1 2026 / Local Tourism Tax (Art. 268 of Tax Code)',
    field1Value: 'І Квартал 2026 року / Туристичний Збір (ст. 268 ПКУ)',

    field2LabelUk: 'Назва готелю / Бази відпочинку & Адреса',
    field2LabelPl: 'Nazwa obiektu noclegowego & Adres',
    field2LabelEn: 'Hotel / Accommodation Facility & Location',
    field2ValueUk: 'Готельно-ресторанний комплекс «Прем\'єр» (м. Львів, вул. Городоцька, 15)',
    field2ValuePl: 'Hotel «Premier» (Lwów, ul. Horodecka 15)',
    field2ValueEn: 'Premier Hotel Complex (Lviv, 15 Horodotska St.)',
    field2Value: 'Готельно-ресторанний комплекс «Прем\'єр» (м. Львів, вул. Городоцька, 15)',

    field3LabelUk: 'Кількість людино-діб ночівлі (Внутрішній / В\'їзний туризм)',
    field3LabelPl: 'Liczba osobodni pobytu (turyści krajowi i zagraniczni)',
    field3LabelEn: 'Number of Guest Bed-Nights (Domestic & Foreign)',
    field3ValueUk: 'Всього: 840 людино-діб (Внутрішній туризм: 620 діб, В\'їзний туризм: 220 діб)',
    field3ValuePl: 'Razem: 840 osobodni (Krajowi: 620, Zagraniczni: 220)',
    field3ValueEn: 'Total: 840 bed-nights (Domestic tourists: 620, Inbound foreign: 220)',
    field3Value: 'Всього: 840 людино-діб (Внутрішній туризм: 620 діб, В\'їзний туризм: 220 діб)',

    field4LabelUk: 'Базова вартість проживання без ПДВ (грн)',
    field4LabelPl: 'Podstawa naliczenia (Wartość noclegów netto, UAH)',
    field4LabelEn: 'Total Base Lodging Revenue Net of VAT (UAH)',
    field4ValueUk: '1 680 000.00 грн (Середня вартість номеру: 2 000 грн/доба)',
    field4ValuePl: '1 680 000.00 UAH (Średnia cena pokoju: 2 000 UAH/doba)',
    field4ValueEn: '1,680,000.00 UAH (Average room rate: 2,000 UAH/night)',
    field4Value: '1 680 000.00 грн (Середня вартість номеру: 2 000 грн/доба)',

    field5LabelUk: 'Ставка туристичного збору (% від мінзарплати: 8 647 грн у 2026 р.)',
    field5LabelPl: 'Stawka opłaty (% od płacy min.: 8 647 UAH w 2026 r.)',
    field5LabelEn: 'Tourism Fee Rate (% of min wage: 8,647 UAH in 2026)',
    field5ValueUk: 'Внутрішній: 0.5% (43.24 грн/доба у 2026 / 40.00 грн у 2025), В\'їзний: 2.5% (216.18 грн/доба у 2026 / 200.00 грн у 2025)',
    field5ValuePl: 'Krajowy: 0.5% (43.24 UAH/doba w 2026 / 40.00 UAH w 2025), Zagraniczny: 2.5% (216.18 UAH/doba w 2026 / 200.00 UAH w 2025)',
    field5ValueEn: 'Domestic: 0.5% (43.24 UAH/night in 2026 / 40.00 UAH in 2025), Foreign: 2.5% (216.18 UAH/night in 2026 / 200.00 UAH in 2025)',
    field5Value: 'Внутрішній: 0.5% (43.24 грн/доба у 2026 / 40.00 грн у 2025), В\'їзний: 2.5% (216.18 грн/доба у 2026 / 200.00 грн у 2025)',

    field6LabelUk: 'Нарахована сума збору до сплати (грн)',
    field6LabelPl: 'Kwota opłaty do wpłaty do budżetu gminy',
    field6LabelEn: 'Total Fee Payable to Municipal Budget',
    field6ValueUk: '68 800.00 грн (Внутрішній: 620 * 40 = 24 800 грн, В\'їзний: 220 * 200 = 44 000 грн)',
    field6ValuePl: '68 800.00 UAH (Krajowi: 24 800 UAH, Zagraniczni: 44 000 UAH)',
    field6ValueEn: '68,800.00 UAH (Domestic: 24,800 UAH, Inbound: 44,000 UAH)',
    field6Value: '68 800.00 грн (Внутрішній: 620 * 40 = 24 800 грн, В\'їзний: 220 * 200 = 44 000 грн)',

    notesTextUk: 'Скласти офіційний проєкт Податкової декларації з туристичного збору за формою J0604107 за І квартал 2026 року. Розрахувати загальну суму збору до сплати в місцевий бюджет (68 800.00 грн), скласти реєстр пільговиків, звільнених від сплати збору (відряджені, учасники бойових дій за пп. 268.2.2 ПКУ) та сформувати звіт у Word (.docx за ДСТУ 4163:2020).',
    notesTextPl: 'Sporządzić deklarację opłaty turystycznej J0604107 za I kwartał 2026 r. Obliczyć kwotę opłaty (68 800 UAH) oraz wygenerować raport w Word (.docx).',
    notesTextEn: 'Prepare official Tourism Tax Declaration (Form J0604107) for Q1 2026. Calculate municipal fee payable (68,800.00 UAH), verify exemptions list under Art. 268.2.2 of Tax Code and compile DSTU 4163:2020 report in Word (.docx).'
  },
  'tax-audit-calendar-agent': {
    field1LabelUk: 'Аналізований період & Дедлайн',
    field1LabelPl: 'Okres analizowany & Terminy',
    field1LabelEn: 'Audit Scope Period & Deadlines',
    field1ValueUk: '2026 рік / Графік перевірок ДПС та звітні квартальні періоди',
    field1ValuePl: '2026 r. / Harmonogram kontroli skarbowych DPS i terminy kwartalne',
    field1ValueEn: 'FY 2026 / DPS Tax Audit Plan & Quarterly Filing Schedule',
    field1Value: '2026 рік / Графік перевірок ДПС та звітні квартальні періоди',

    field2LabelUk: 'Вид аудиту & Ступінь ризику (Наказ № 524)',
    field2LabelPl: 'Rodzaj kontroli & Poziom ryzyka (Rozporządzenie nr 524)',
    field2LabelEn: 'Audit Category & Risk Tier (Order No. 524)',
    field2ValueUk: 'Планова документальна перевірка ДПС / Середній ступінь ризику (35 балів)',
    field2ValuePl: 'Planowana kontrola skarbowa / Średni poziom ryzyka (35 pkt)',
    field2ValueEn: 'Scheduled Routine Tax Audit / Medium Risk Tier (35 points)',
    field2Value: 'Планова документальна перевірка ДПС / Середній ступінь ризику (35 балів)',

    field3LabelUk: 'Податкове навантаження з ПДВ & Прибутку (%)',
    field3LabelPl: 'Obciążenie podatkowe VAT & CIT (%)',
    field3LabelEn: 'Effective Tax Burden for VAT & CIT (%)',
    field3ValueUk: 'ПДВ: 2.10% (середньогалузеве: 2.80%), Прибуток: 1.15% (середньогалузеве: 1.60%)',
    field3ValuePl: 'VAT: 2.10% (średnia branżowa: 2.80%), CIT: 1.15% (średnia: 1.60%)',
    field3ValueEn: 'VAT: 2.10% (sector avg: 2.80%), CIT: 1.15% (sector avg: 1.60%)',
    field3Value: 'ПДВ: 2.10% (середньогалузеве: 2.80%), Прибуток: 1.15% (середньогалузеве: 1.60%)',

    field4LabelUk: 'Співвідношення дебіторської та кредиторської заборгованості',
    field4LabelPl: 'Relacja należności do zobowiązań',
    field4LabelEn: 'Receivables to Payables Ratio',
    field4ValueUk: 'Дебіторка: 14 200 000 грн ↔ Кредиторка: 6 100 000 грн (Коефіцієнт: 2.33 — тригер перевірки)',
    field4ValuePl: 'Należności: 14.2 mln UAH ↔ Zobowiązania: 6.1 mln UAH (Współczynnik: 2.33 — ryzyko)',
    field4ValueEn: 'Receivables: 14.2M UAH ↔ Payables: 6.1M UAH (Ratio: 2.33 — inspection trigger)',
    field4Value: 'Дебіторка: 14 200 000 грн ↔ Кредиторка: 6 100 000 грн (Коефіцієнт: 2.33 — тригер перевірки)',

    field5LabelUk: 'Наявність зупинених ПН / Від\'ємного значення',
    field5LabelPl: 'Zablokowane faktury / Wynik ujemny',
    field5LabelEn: 'Suspended Invoices / Consecutive Negative Margin',
    field5ValueUk: '1 заблокована ПН на суму 120 000 грн ПДВ, від\'ємне значення об\'єкта прибутку відсутнє',
    field5ValuePl: '1 zablokowana faktura VAT na 120 000 UAH, brak straty podatkowej',
    field5ValueEn: '1 suspended VAT invoice for 120,000 UAH, zero negative taxable profit',
    field5Value: '1 заблокована ПН на суму 120 000 грн ПДВ, від\'ємне значення об\'єкта прибутку відсутнє',

    field6LabelUk: 'Коди доходів бюджету (КБКБ) & Граничні строки',
    field6LabelPl: 'Kody klasyfikacji budżetowej (KBKB) & Terminy',
    field6LabelEn: 'Budget Classification Codes (KBKB) & Deadlines',
    field6ValueUk: 'КБКБ 11021000 (Податок на прибуток), 14010100 (ПДВ), 11010100 (ПДФО), 11011000 (Військовий збір)',
    field6ValuePl: 'Kody KBKB: 11021000 (CIT), 14010100 (VAT), 11010100 (PIT), 11011000 (Składka wojskowa)',
    field6ValueEn: 'KBKB Codes: 11021000 (CIT), 14010100 (VAT), 11010100 (PIT), 11011000 (Military Tax)',
    field6Value: 'КБКБ 11021000 (Податок на прибуток), 14010100 (ПДВ), 11010100 (ПДФО), 11011000 (Військовий збір)',

    notesTextUk: 'Провести комплексний скоринг ризиків включення компанії до плану-графіка документальних планових перевірок ДПС України на 2026 рік за Наказом Мінфіну № 524. Проаналізувати розрив податкового навантаження з ПДВ і прибутку порівняно з галузевим, оцінити ризик за співвідношенням заборгованостей, скласти детальний календар звітності/сплати податків та сформувати захисний план заходів у Word (.docx за ДСТУ 4163:2020).',
    notesTextPl: 'Przeprowadzić kompleksowy audyt ryzyka wpisania firmy do planu kontroli skarbowych DPS na 2026 r. wg Rozporządzenia nr 524. Opracować kalendarz podatkowy i plan ochrony w Word (.docx).',
    notesTextEn: 'Execute full risk scoring audit for DPS Tax Inspection Plan inclusion for FY 2026 under Order No. 524. Benchmark VAT and CIT tax burden against sector medians, map debt imbalance triggers, draft calendar schedule and deliver strategic defense action plan under DSTU 4163:2020 in Word (.docx).'
  },
  'tax-financial-analyst': {
    field1LabelUk: 'Звітний Період & Галузь підприємства',
    field1LabelPl: 'Okres sprawozdawczy & Branża',
    field1LabelEn: 'Reporting Period & Industry Sector',
    field1ValueUk: '2025-2026 рр. / Машинобудування та металообробка (КВЕД 25.11)',
    field1ValuePl: '2025-2026 r. / Produkcja konstrukcji metalowych (PKD 25.11)',
    field1ValueEn: 'FY 2025-2026 / Metal Fabrication & Engineering (NACE 25.11)',
    field1Value: '2025-2026 рр. / Машинобудування та металообробка (КВЕД 25.11)',

    field2LabelUk: 'Форма власності & Система оподаткування',
    field2LabelPl: 'Forma prawna & System podatkowy',
    field2LabelEn: 'Legal Form & Tax Regime',
    field2ValueUk: 'ТОВ на загальній системі оподаткування (Платник ПДВ 20%, Податок на прибуток 18%)',
    field2ValuePl: 'Sp. z o.o. na zasadach ogólnych (VAT 20%, CIT 18%)',
    field2ValueEn: 'LLC on General Taxation System (20% VAT, 18% Corporate Income Tax)',
    field2Value: 'ТОВ на загальній системі оподаткування (Платник ПДВ 20%, Податок на прибуток 18%)',

    field3LabelUk: 'Валовий дохід підприємства (грн)',
    field3LabelPl: 'Przychód ze sprzedaży (UAH)',
    field3LabelEn: 'Gross Commercial Revenue (UAH)',
    field3ValueUk: '38 500 000.00 грн (Реалізація металоконструкцій в Україні: 28.5 млн грн, експорт: 10 млн грн)',
    field3ValuePl: '38 500 000.00 UAH (Sprzedaż krajowa: 28.5 mln UAH, eksport: 10 mln UAH)',
    field3ValueEn: '38,500,000.00 UAH (Domestic sales: 28.5M UAH, zero-rate exports: 10M UAH)',
    field3Value: '38 500 000.00 грн (Реалізація металоконструкцій в Україні: 28.5 млн грн, експорт: 10 млн грн)',

    field4LabelUk: 'Сукупні витрати та собівартість (грн)',
    field4LabelPl: 'Koszty całkowite i koszt własny (UAH)',
    field4LabelEn: 'Total Operating Expenses & COGS (UAH)',
    field4ValueUk: '31 200 000.00 грн (Сировина: 18 млн грн, ФОП: 6.8 млн грн, амортизація: 2.4 млн грн, збут: 4 млн грн)',
    field4ValuePl: '31 200 000.00 UAH (Materiały: 18 mln, płace: 6.8 mln, amortyzacja: 2.4 mln)',
    field4ValueEn: '31,200,000.00 UAH (Raw materials: 18M, payroll: 6.8M, depreciation: 2.4M, SG&A: 4M)',
    field4Value: '31 200 000.00 грн (Сировина: 18 млн грн, ФОП: 6.8 млн грн, амортизація: 2.4 млн грн, збут: 4 млн грн)',

    field5LabelUk: 'Фонд оплати праці та кількість працівників',
    field5LabelPl: 'Fundusz płac & Liczba pracowników',
    field5LabelEn: 'Payroll Fund & Total Headcount',
    field5ValueUk: '6 800 000.00 грн / рік (32 штатні працівники, середня зарплата: 17 700 грн)',
    field5ValuePl: '6 800 000.00 UAH / rok (32 pracowników, średnia płaca: 17 700 UAH)',
    field5ValueEn: '6,800,000.00 UAH / year (32 staff, average wage: 17,700 UAH)',
    field5Value: '6 800 000.00 грн / рік (32 штатні працівники, середня зарплата: 17 700 грн)',

    field6LabelUk: 'Оціночне податкове навантаження (%) & Сплачено',
    field6LabelPl: 'Efektywna stawka podatkowa (%) & Wpłacono',
    field6LabelEn: 'Effective Tax Burden (%) & Total Paid',
    field6ValueUk: 'Податок на прибуток: 1 314 000 грн (3.41%), ПДВ до сплати: 1 150 000 грн (2.98%)',
    field6ValuePl: 'CIT: 1 314 000 UAH (3.41%), VAT: 1 150 000 UAH (2.98%)',
    field6ValueEn: 'CIT: 1,314,000 UAH (3.41%), VAT: 1,150,000 UAH (2.98%)',
    field6Value: 'Податок на прибуток: 1 314 000 грн (3.41%), ПДВ до сплати: 1 150 000 грн (2.98%)',

    notesTextUk: 'Провести повний податково-фінансовий аудит діяльності виробничого підприємства за 2025-2026 роки. Розрахувати реальне податкове навантаження з ПДВ, податку на прибуток та фонду оплати праці, провести стрес-тестування структури витрат та сформувати Стратегічний податковий план оптимізації у Word (.docx за ДСТУ 4163:2020) та Excel (.xlsx).',
    notesTextPl: 'Przeprowadzić audyt podatkowo-finansowy przedsiębiorstwa produkcyjnego za lata 2025-2026. Opracować plan optymalizacji podatkowej w Word (.docx) i Excel (.xlsx).',
    notesTextEn: 'Execute comprehensive financial and tax diagnostic audit for manufacturing enterprise for 2025-2026. Calculate effective tax ratios for VAT, CIT and labor taxes, model cost structures and compile Strategic Tax Plan under DSTU 4163:2020 in Word (.docx) and Excel (.xlsx).'
  },
  'public-accountant-kekv': {
    field1LabelUk: 'Бюджетний Період & Джерело Фінансування',
    field1LabelPl: 'Okres budżetowy & Źródło finansowania',
    field1LabelEn: 'Budget Period & Funding Source',
    field1ValueUk: '2026 рік / Загальний фонд державного бюджету (КПКВК 2201010)',
    field1ValuePl: '2026 r. / Fundusz ogólny budżetu państwa',
    field1ValueEn: 'FY 2026 / General State Budget Fund (Program Code 2201010)',
    field1Value: '2026 рік / Загальний фонд державного бюджету (КПКВК 2201010)',

    field2LabelUk: 'Коди КЕКВ для класифікації видатків',
    field2LabelPl: 'Kody klasyfikacji wydatków KEKV',
    field2LabelEn: 'KEKV Economic Expense Codes',
    field2ValueUk: 'КЕКВ 2210 (Предмети, матеріали), КЕКВ 2240 (Оплата послуг), КЕКВ 3110 (Придбання ОЗ)',
    field2ValuePl: 'KEKV 2210 (Materiały), KEKV 2240 (Usługi), KEKV 3110 (Środki trwałe)',
    field2ValueEn: 'KEKV 2210 (Supplies), KEKV 2240 (Services), KEKV 3110 (Capital Equipment)',
    field2Value: 'КЕКВ 2210 (Предмети, матеріали), КЕКВ 2240 (Оплата послуг), КЕКВ 3110 (Придбання ОЗ)',

    field3LabelUk: 'Предмет закупівлі / Господарська операція',
    field3LabelPl: 'Przedmiot zamówienia publicznego',
    field3LabelEn: 'Procurement Object / Operational Purpose',
    field3ValueUk: 'Модернізація серверного комплексу та мережевого обладнання, монтажні та пусконалагоджувальні роботи',
    field3ValuePl: 'Modernizacja serwerów i sieci, prace montażowe i uruchomieniowe',
    field3ValueEn: 'Server infrastructure & network upgrade, installation and commissioning',
    field3Value: 'Модернізація серверного комплексу та мережевого обладнання, монтажні та пусконалагоджувальні роботи',

    field4LabelUk: 'Запланована сума видатків кошторису (грн)',
    field4LabelPl: 'Planowana kwota wydatków (UAH)',
    field4LabelEn: 'Budgeted Expenditure Total (UAH)',
    field4ValueUk: '1 450 000.00 грн (КЕКВ 3110: 1 150 000 грн, КЕКВ 2240: 200 000 грн, КЕКВ 2210: 100 000 грн)',
    field4ValuePl: '1 450 000.00 UAH (KEKV 3110: 1.15 mln, KEKV 2240: 200 tys., KEKV 2210: 100 tys.)',
    field4ValueEn: '1,450,000.00 UAH (KEKV 3110: 1.15M, KEKV 2240: 200k, KEKV 2210: 100k)',
    field4Value: '1 450 000.00 грн (КЕКВ 3110: 1 150 000 грн, КЕКВ 2240: 200 000 грн, КЕКВ 2210: 100 000 грн)',

    field5LabelUk: 'Назва бюджетної установи & ЄДРПОУ',
    field5LabelPl: 'Nazwa jednostki budżetowej & REGON',
    field5LabelEn: 'Public Institution Name & EDRPOU Code',
    field5ValueUk: 'Державне науково-дослідне підприємство «Центр інформаційних систем» (ЄДРПОУ 24891028)',
    field5ValuePl: 'Państwowa jednostka badawcza «Centrum Systemów IT» (EDRPOU 24891028)',
    field5ValueEn: 'State R&D Enterprise «Center of Information Systems» (EDRPOU 24891028)',
    field5Value: 'Державне науково-дослідне підприємство «Центр інформаційних систем» (ЄДРПОУ 24891028)',

    field6LabelUk: 'Реєстраційний рахунок у Казначействі (IBAN)',
    field6LabelPl: 'Rachunek w Izbie Skarbowej / Kasie (IBAN)',
    field6LabelEn: 'State Treasury Account Number (IBAN)',
    field6ValueUk: 'UA82820100000352100000008491 (ГУ ДКСУ у м. Києві)',
    field6ValuePl: 'UA82820100000352100000008491 (Izba Skarbowa m. Kijów)',
    field6ValueEn: 'UA82820100000352100000008491 (State Treasury Administration Kyiv)',
    field6Value: 'UA82820100000352100000008491 (ГУ ДКСУ у м. Києві)',

    notesTextUk: 'Здійснити експертний підбір та обґрунтування застосування кодів економічної класифікації видатків (КЕКВ 2210, 2240, 3110) за Інструкцією Мінфіну № 333 та Постановою КМУ № 228. Підготувати пакет обґрунтування для реєстрації бюджетних та фінансових зобов\'язань в органах Держказначейства у Word (.docx за ДСТУ 4163:2020).',
    notesTextPl: 'Przeprowadzić dobór i uzasadnienie kodów KEKV wg Instrukcji nr 333 dla rejestracji zobowiązań w Kasie Państwowej w Word (.docx).',
    notesTextEn: 'Execute regulatory determination and justification of KEKV expenditure codes (KEKV 2210, 2240, 3110) under MoF Instruction No. 333 and CMU Res. No. 228. Draft official registration brief for State Treasury commitments in Word (.docx DSTU 4163:2020).'
  },
  'agri-mpz-calculator': {
    field1LabelUk: 'Звітний Податковий Період (Рік)',
    field1LabelPl: 'Podatkowy okres rozliczeniowy (Rok)',
    field1LabelEn: 'Tax Assessment Period (Year)',
    field1ValueUk: '2026 рік (Річний розрахунок МПЗ за ст. 38-1 ПКУ)',
    field1ValuePl: '2026 r. (Roczne rozliczenie MPZ wg art. 38-1 PKU)',
    field1ValueEn: 'FY 2026 (Annual MPZ calculation under Art. 38-1 of Tax Code)',
    field1Value: '2026 рік (Річний розрахунок МПЗ за ст. 38-1 ПКУ)',

    field2LabelUk: 'Загальна площа земель / паїв (га)',
    field2LabelPl: 'Łączna powierzchnia gruntów rolnych (ha)',
    field2LabelEn: 'Total Agricultural Land Bank (Hectares)',
    field2ValueUk: '540.2500 га (Рілля: 480.00 га, Пасовища: 60.25 га в оренді агропідприємства)',
    field2ValuePl: '540.2500 ha (Grunty orne: 480.00 ha, pastwiska: 60.25 ha)',
    field2ValueEn: '540.2500 ha (Arable cropland: 480.00 ha, pasture: 60.25 ha under lease)',
    field2Value: '540.2500 га (Рілля: 480.00 га, Пасовища: 60.25 га в оренді агропідприємства)',

    field3LabelUk: 'Загальна сума НГО ділянок (грн)',
    field3LabelPl: 'Wartość szacunkowa gruntów (NGO, UAH)',
    field3LabelEn: 'Total Monetary Valuation of Land (NGO, UAH)',
    field3ValueUk: '16 850 000.00 грн (З урахуванням коефіцієнтів індексації ДЗК)',
    field3ValuePl: '16 850 000.00 UAH (Z uwzględnieniem waloryzacji katastru DZK)',
    field3ValueEn: '16,850,000.00 UAH (Indexed as per State Land Cadastre)',
    field3Value: '16 850 000.00 грн (З урахуванням коефіцієнтів індексації ДЗК)',

    field4LabelUk: 'Сплачено Єдиного Податку 4 групи (грн)',
    field4LabelPl: 'Zapłacony podatek rolny 4 grupy (UAH)',
    field4LabelEn: 'Paid Single Agricultural Tax Group 4 (UAH)',
    field4ValueUk: '134 800.00 грн (Сплачено за звітний податковий рік)',
    field4ValuePl: '134 800.00 UAH (Wpłacono za rok podatkowy)',
    field4ValueEn: '134,800.00 UAH (Remitted for the assessment year)',
    field4Value: '134 800.00 грн (Сплачено за звітний податковий рік)',

    field5LabelUk: 'Сплачено ПДФО, ВЗ та Земельного податку (грн)',
    field5LabelPl: 'Zapłacony PIT, składka wojskowa i podatek gruntowy',
    field5LabelEn: 'Paid PIT, Military Tax & Land Tax on Leases',
    field5ValueUk: '285 000.00 грн (ПДФО 18% та ВЗ 5% з орендної плати за земельні паї громадянам)',
    field5ValuePl: '285 000.00 UAH (PIT i składka wojskowa z dzierżawy)',
    field5ValueEn: '285,000.00 UAH (18% PIT & 5% Military Tax paid on farmland rent)',
    field5Value: '285 000.00 грн (ПДФО 18% та ВЗ 5% з орендної плати за земельні паї громадянам)',

    field6LabelUk: 'Кадастрові номери ділянок / Реєстр договорів',
    field6LabelPl: 'Numery działek & Rejestr umów dzierżawy',
    field6LabelEn: 'Cadastral Parcel Numbers & Lease Register',
    field6ValueUk: '3222485800:02:001:0012, 3222485800:02:001:0015 (18 зареєстрованих договорів оренди)',
    field6ValuePl: '3222485800:02:001:0012 i inne (18 zarejestrowanych umów dzierżawy)',
    field6ValueEn: '3222485800:02:001:0012, 3222485800:02:001:0015 (18 registered lease deeds)',
    field6Value: '3222485800:02:001:0012, 3222485800:02:001:0015 (18 зареєстрованих договорів оренди)',

    notesTextUk: 'Провести повний автоматичний розрахунок Мінімального Податкового Зобов\'язання (МПЗ) за ст. 38-1 ПКУ для агропідприємства за 2026 рік. Застосувати коефіцієнт K=0.05 (НГО 16.85 млн грн * 0.05 = 842 500 грн базового МПЗ). Провести залік сплачених податків (ЄП 4 групи 134 800 грн + ПДФО/ВЗ з оренди 285 000 грн = 419 800 грн). Розрахувати позитивну різницю до доплати в бюджет (842 500 - 419 800 = 422 700 грн) та сформувати Додаток МПЗ у Word (.docx за ДСТУ 4163:2020) та Excel (.xlsx).',
    notesTextPl: 'Sporządzić rozliczenie Minimalnego Zobowiązania Podatkowego (MPZ) wg art. 38-1 PKU za 2026 r. Naliczyć podatek bazowy z wskaźnikiem K=0.05 (842 500 UAH), odliczyć wpłacone podatki (419 800 UAH), ustalić dopłatę (422 700 UAH) i wygenerować raport w Word (.docx) i Excel (.xlsx).',
    notesTextEn: 'Execute automated Minimum Tax Commitment (MPZ) determination under Art. 38-1 of Tax Code for FY 2026. Apply statutory coefficient K=0.05 (Base MPZ: 842,500 UAH), offset qualifying paid taxes (419,800 UAH), calculate net state budget payable difference (422,700 UAH) and compile official MPZ Schedule in Word (.docx DSTU 4163:2020) and Excel (.xlsx).'
  },
  'cfo-financial-analyzer': {
    field1LabelUk: 'Аналізований фінансовий період & Галузь',
    field1LabelPl: 'Okres sprawozdawczy & Branża',
    field1LabelEn: 'Reporting Period & Industry Sector',
    field1ValueUk: 'І Квартал 2026 року (3 місяці) / Виробництво та дистриб\'юція FMCG',
    field1ValuePl: 'I Kwartał 2026 r. (3 miesiące) / Produkcja i dystrybucja FMCG',
    field1ValueEn: 'Q1 2026 (3 Months) / FMCG Manufacturing & Distribution',
    field1Value: 'І Квартал 2026 року (3 місяці) / Виробництво та дистриб\'юція FMCG',

    field2LabelUk: 'Загальний валовий дохід / Виручка (грн)',
    field2LabelPl: 'Przychody ze sprzedaży netto (UAH)',
    field2LabelEn: 'Gross Commercial Net Revenue (UAH)',
    field2ValueUk: '24 800 000.00 грн (Зростання на +14.5% до аналогічного періоду минулого року)',
    field2ValuePl: '24 800 000.00 UAH (+14.5% r/r)',
    field2ValueEn: '24,800,000.00 UAH (+14.5% YoY growth)',
    field2Value: '24 800 000.00 грн (Зростання на +14.5% до аналогічного періоду минулого року)',

    field3LabelUk: 'Собівартість & Операційні витрати OPEX (грн)',
    field3LabelPl: 'Koszty wytworzenia & Koszty operacyjne OPEX',
    field3LabelEn: 'Cost of Goods Sold (COGS) & OPEX (UAH)',
    field3ValueUk: 'Собівартість COGS: 14 500 000 грн, Операційні витрати (адмін + збут): 5 800 000 грн',
    field3ValuePl: 'COGS: 14 500 000 UAH, OPEX (koszty ogólnego zarządu i sprzedaży): 5 800 000 UAH',
    field3ValueEn: 'COGS: 14,500,000 UAH, OPEX (SG&A): 5,800,000 UAH',
    field3Value: 'Собівартість COGS: 14 500 000 грн, Операційні витрати (адмін + збут): 5 800 000 грн',

    field4LabelUk: 'Операційний прибуток EBITDA (грн) & Маржа',
    field4LabelPl: 'Zysk operacyjny EBITDA & Marża EBITDA',
    field4LabelEn: 'Operating EBITDA & EBITDA Margin (%)',
    field4ValueUk: 'EBITDA: 5 400 000.00 грн (Маржинальність EBITDA: 21.77%)',
    field4ValuePl: 'EBITDA: 5 400 000.00 UAH (Marża EBITDA: 21.77%)',
    field4ValueEn: 'EBITDA: 5,400,000.00 UAH (EBITDA Margin: 21.77%)',
    field4Value: 'EBITDA: 5 400 000.00 грн (Маржинальність EBITDA: 21.77%)',

    field5LabelUk: 'Чистий грошовий потік (Cash Flow) & Ліквідність',
    field5LabelPl: 'Przepływy pieniężne (Cash Flow) & Płynność',
    field5LabelEn: 'Net Cash Flow & Working Capital Ratios',
    field5ValueUk: 'Операційний Cash Flow (OCF): +3 850 000 грн, Поточна ліквідність (Current Ratio): 1.82',
    field5ValuePl: 'Operacyjny Cash Flow (OCF): +3 850 000 UAH, Wskaźnik płynności bieżącej: 1.82',
    field5ValueEn: 'Operating Cash Flow (OCF): +3,850,000 UAH, Current Liquidity Ratio: 1.82',
    field5Value: 'Операційний Cash Flow (OCF): +3 850 000 грн, Поточна ліквідність (Current Ratio): 1.82',

    field6LabelUk: 'Боргове навантаження (Net Debt / EBITDA) & Altman Z',
    field6LabelPl: 'Zadłużenie (Dług netto / EBITDA) & Altman Z-Score',
    field6LabelEn: 'Debt Leverage (Net Debt / EBITDA) & Altman Z',
    field6ValueUk: 'Net Debt / EBITDA = 1.15 (Норма < 2.5), Модель Альтмана Z-Score = 3.45 (Зелена зона стабільності)',
    field6ValuePl: 'Dług netto / EBITDA = 1.15, Altman Z-Score = 3.45 (Strefa bezpieczna)',
    field6ValueEn: 'Net Debt / EBITDA = 1.15 (Healthy < 2.5), Altman Z-Score = 3.45 (Safe Green Zone)',
    field6Value: 'Net Debt / EBITDA = 1.15 (Норма < 2.5), Модель Альтмана Z-Score = 3.45 (Зелена зона стабільності)',

    notesTextUk: 'Сформувати комплексний Аналітичний фінансовий звіт CFO (P&L, Cash Flow, Баланс та EBITDA) за І квартал 2026 року. Розрахувати коефіцієнти поточної та швидкої ліквідності, оборотність робочого капіталу (NWC), провести факторний аналіз маржинальності та оцінку ризику банкрутства за моделлю Альтмана Z-Score. Оформити висновок для Ради директорів у Word (.docx за ДСТУ 4163:2020) та Excel (.xlsx).',
    notesTextPl: 'Sporządzić kompleksowy raport dyrektora finansowego CFO (RZiS, Cash Flow, EBITDA, Altman Z-Score) za I kwartał 2026 r. w Word (.docx) i Excel (.xlsx).',
    notesTextEn: 'Prepare comprehensive CFO Executive Financial Intelligence Report (P&L, Cash Flow, EBITDA, Altman Z-Score) for Q1 2026. Analyze liquidity, operating working capital turnover and deliver board presentation pack in Word (.docx DSTU 4163:2020) and Excel (.xlsx).'
  },
  'budget-program-passport': {
    field1LabelUk: 'Назва бюджетної програми & КПКВК',
    field1LabelPl: 'Nazwa programu budżetowego & Kod KPKVK',
    field1LabelEn: 'Budget Program Title & KPKVK Code',
    field1ValueUk: 'КПКВК 0611020 «Надання загальної середньої освіти закладами загальної середньої освіти»',
    field1ValuePl: 'KPKVK 0611020 «Zapewnienie powszechnego szkolnictwa średniego»',
    field1ValueEn: 'KPKVK 0611020 «Provision of Secondary Education in Municipal Schools»',
    field1Value: 'КПКВК 0611020 «Надання загальної середньої освіти закладами загальної середньої освіти»',

    field2LabelUk: 'Головний розпорядник бюджетних коштів (ГРК)',
    field2LabelPl: 'Główny dysponent środków budżetowych',
    field2LabelEn: 'Chief Budget Beneficiary / Entity (GRK)',
    field2ValueUk: 'Управління освіти і науки міської ради (код за ЄДРПОУ: 02148392)',
    field2ValuePl: 'Wydział Edukacji i Nauki Rady Miejskiej (EDRPOU: 02148392)',
    field2ValueEn: 'Department of Education and Science of City Council (EDRPOU: 02148392)',
    field2Value: 'Управління освіти і науки міської ради (код за ЄДРПОУ: 02148392)',

    field3LabelUk: 'Обсяг бюджетного призначення (Загальний/Спецфонд)',
    field3LabelPl: 'Przydział budżetowy (Fundusz ogólny / specjalny)',
    field3LabelEn: 'Budget Allocation (General / Special Fund)',
    field3ValueUk: 'Загальний фонд: 42 500 000.00 грн, Спеціальний фонд: 3 800 000.00 грн',
    field3ValuePl: 'Fundusz ogólny: 42 500 000.00 UAH, Fundusz specjalny: 3 800 000.00 UAH',
    field3ValueEn: 'General Fund: 42,500,000.00 UAH, Special Fund: 3,800,000.00 UAH',
    field3Value: 'Загальний фонд: 42 500 000.00 грн, Спеціальний фонд: 3 800 000.00 грн',

    field4LabelUk: 'Показники затрат & продукту (Кількість)',
    field4LabelPl: 'Wskaźniki nakładów i produktu (Liczba)',
    field4LabelEn: 'Expenditure & Output Indicators (Count)',
    field4ValueUk: 'Кількість закладів: 14 ліцеїв, Кількість учнів: 8 450 осіб',
    field4ValuePl: 'Liczba placówek: 14 liceów, Liczba uczniów: 8 450 osób',
    field4ValueEn: 'Number of schools: 14 lyceums, Total student body: 8,450 students',
    field4Value: 'Кількість закладів: 14 ліцеїв, Кількість учнів: 8 450 осіб',

    field5LabelUk: 'Показники ефективності (Кошти на 1 учня)',
    field5LabelPl: 'Wskaźniki efektywności (Koszt na 1 ucznia)',
    field5LabelEn: 'Efficiency Indicators (Cost per Student)',
    field5ValueUk: 'Середні витрати на 1 учня: 5 029.58 грн / рік',
    field5ValuePl: 'Średni koszt na 1 ucznia: 5 029.58 UAH / rok',
    field5ValueEn: 'Average cost per student: 5,029.58 UAH / year',
    field5Value: 'Середні витрати на 1 учня: 5 029.58 грн / рік',

    field6LabelUk: 'Показники якості (% охоплення / виконання)',
    field6LabelPl: 'Wskaźniki jakości (% wykonania)',
    field6LabelEn: 'Quality Indicators (% Attainment / Coverage)',
    field6ValueUk: '100% забезпечення навчальними підручниками та комп’ютерною технікою',
    field6ValuePl: '100% zapewnienie podręczników i sprzętu komputerowego',
    field6ValueEn: '100% provision with textbooks and digital computing workstations',
    field6Value: '100% забезпечення навчальними підручниками та комп’ютерною технікою',

    notesTextUk: 'Скласти та проаналізувати Паспорт бюджетної програми за Наказом Мінфіну № 371 та ст. 20 Бюджетного кодексу України на 2026 рік. Розрахувати всі 4 групи результативних показників (затрат, продукту, ефективності, якості), перевірити відповідність цілям та сформувати Паспорт і Звіт про виконання у Word (.docx) та Excel (.xlsx) за ДСТУ 4163:2020.',
    notesTextPl: 'Sporządzić paszport programu budżetowego wg Rozporządzenia nr 371 i Kodeksu Budżetowego w Word (.docx) i Excel (.xlsx).',
    notesTextEn: 'Compile and audit Public Budget Program Passport under MoF Order No. 371 and Art. 20 of Budget Code of Ukraine for FY 2026. Calculate all 4 groups of performance metrics (expenditure, output, efficiency, quality) and generate official DSTU 4163:2020 documentation in Word (.docx) and Excel (.xlsx).'
  },
  'budget-koshtorys-builder': {
    field1LabelUk: 'Назва установи & Код ЄДРПОУ',
    field1LabelPl: 'Nazwa jednostki & Kod REGON/EDRPOU',
    field1LabelEn: 'Institution Name & EDRPOU Registry Code',
    field1ValueUk: 'Державна установа «Обласний центр контролю та профілактики хвороб» (ЄДРПОУ: 38492019)',
    field1ValuePl: 'Państwowa instytucja «Wojewódzkie Centrum Kontroli Chorób» (EDRPOU: 38492019)',
    field1ValueEn: 'State Institution «Regional Center for Disease Control» (EDRPOU: 38492019)',
    field1Value: 'Державна установа «Обласний центр контролю та профілактики хвороб» (ЄДРПОУ: 38492019)',

    field2LabelUk: 'Загальна сума кошторису на рік (грн)',
    field2LabelPl: 'Łączna kwota planu finansowego na rok (UAH)',
    field2LabelEn: 'Total Annual Budget Estimate (UAH)',
    field2ValueUk: '18 600 000.00 грн (Загальний фонд: 15 200 000 грн, Спецфонд: 3 400 000 грн)',
    field2ValuePl: '18 600 000.00 UAH (Fundusz ogólny: 15.2 mln, specjalny: 3.4 mln)',
    field2ValueEn: '18,600,000.00 UAH (General Fund: 15.2M UAH, Special Fund: 3.4M UAH)',
    field2Value: '18 600 000.00 грн (Загальний фонд: 15 200 000 грн, Спецфонд: 3 400 000 грн)',

    field3LabelUk: 'Видатки на оплату праці (КЕКВ 2111 / 2120)',
    field3LabelPl: 'Wydatki na wynagrodzenia (KEKV 2111 / 2120)',
    field3LabelEn: 'Labor & Social Security Outlays (KEKV 2111 / 2120)',
    field3ValueUk: 'КЕКВ 2111 (Заробітна плата): 11 400 000 грн, КЕКВ 2120 (Нарахування ЄСВ 22%): 2 508 000 грн',
    field3ValuePl: 'KEKV 2111 (Płace): 11 400 000 UAH, KEKV 2120 (ZUS 22%): 2 508 000 UAH',
    field3ValueEn: 'KEKV 2111 (Wages): 11,400,000 UAH, KEKV 2120 (ESV 22%): 2,508,000 UAH',
    field3Value: 'КЕКВ 2111 (Заробітна плата): 11 400 000 грн, КЕКВ 2120 (Нарахування ЄСВ 22%): 2 508 000 грн',

    field4LabelUk: 'Комунальні послуги & Енергоносії (КЕКВ 2270)',
    field4LabelPl: 'Opłaty komunalne & Energia (KEKV 2270)',
    field4LabelEn: 'Utilities & Heating Outlays (KEKV 2270)',
    field4ValueUk: 'КЕКВ 2271 (Теплопостачання): 850 000 грн, КЕКВ 2273 (Електроенергія): 620 000 грн',
    field4ValuePl: 'KEKV 2271 (Ciepło): 850 000 UAH, KEKV 2273 (Energia elektryczna): 620 000 UAH',
    field4ValueEn: 'KEKV 2271 (Central Heating): 850,000 UAH, KEKV 2273 (Electricity): 620,000 UAH',
    field4Value: 'КЕКВ 2271 (Теплопостачання): 850 000 грн, КЕКВ 2273 (Електроенергія): 620 000 грн',

    field5LabelUk: 'Предмети, матеріали & Послуги (КЕКВ 2210/2240)',
    field5LabelPl: 'Materiały i usługi (KEKV 2210 / 2240)',
    field5LabelEn: 'Supplies & Contractual Services (KEKV 2210/2240)',
    field5ValueUk: 'КЕКВ 2210 (Канцтовари/Реактиви): 480 000 грн, КЕКВ 2240 (Оплата послуг): 740 000 грн',
    field5ValuePl: 'KEKV 2210 (Materiały): 480 000 UAH, KEKV 2240 (Usługi): 740 000 UAH',
    field5ValueEn: 'KEKV 2210 (Supplies/Reagents): 480,000 UAH, KEKV 2240 (Services): 740,000 UAH',
    field5Value: 'КЕКВ 2210 (Канцтовари/Реактиви): 480 000 грн, КЕКВ 2240 (Оплата послуг): 740 000 грн',

    field6LabelUk: 'Капітальні видатки (КЕКВ 3110 Придбання ОЗ)',
    field6LabelPl: 'Wydatki majątkowe (KEKV 3110 Środki trwałe)',
    field6LabelEn: 'Capital Expenditures (KEKV 3110 Fixed Assets)',
    field6ValueUk: 'КЕКВ 3110 (Лабораторне діагностичне обладнання): 2 002 000 грн (Спеціальний фонд)',
    field6ValuePl: 'KEKV 3110 (Aparatura laboratoryjna): 2 002 000 UAH (Fundusz specjalny)',
    field6ValueEn: 'KEKV 3110 (Laboratory Diagnostic Hardware): 2,002,000 UAH (Special Fund)',
    field6Value: 'КЕКВ 3110 (Лабораторне діагностичне обладнання): 2 002 000 грн (Спеціальний фонд)',

    notesTextUk: 'Сформувати повний Кошторис бюджетної установи на 2026 рік, План асигнувань та Розрахунки до кошторису розпорядника бюджетних коштів за Наказом Мінфіну № 57 та Постановою КМУ № 228. Підготувати пакет для реєстрації у Державній казначейській службі України (ДКСУ) у Excel (.xlsx) та Word (.docx за ДСТУ 4163:2020).',
    notesTextPl: 'Sporządzić plan finansowy jednostki budżetowej na 2026 r. wg Rozporządzenia nr 57 i Uchwały KMU 228 w Word (.docx) i Excel (.xlsx).',
    notesTextEn: 'Prepare complete Public Budget Estimate (Koshtorys) for FY 2026, Appropriation Schedule and economic calculations under MoF Order No. 57 and CMU Res. No. 228. Produce registration package for State Treasury of Ukraine in Word (.docx DSTU 4163:2020) and Excel (.xlsx).'
  },
  'npsbods-accounting-auditor': {
    field1LabelUk: 'Назва бюджетної установи & Організація',
    field1LabelPl: 'Nazwa jednostki sektora finansów publicznych',
    field1LabelEn: 'Public Sector Institution Name',
    field1ValueUk: 'Комунальне некомерційне підприємство «Міська клінічна лікарня № 4» (ЄДРПОУ 01984210)',
    field1ValuePl: 'Samodzielny Publiczny Zakład Opieki Zdrowotnej «Szpital Miejski nr 4»',
    field1ValueEn: 'Municipal Non-Profit Clinical Hospital No. 4 (EDRPOU 01984210)',
    field1Value: 'Комунальне некомерційне підприємство «Міська клінічна лікарня № 4» (ЄДРПОУ 01984210)',

    field2LabelUk: 'Звітний період & Форма звітності',
    field2LabelPl: 'Okres rozliczeniowy & Sprawozdanie',
    field2LabelEn: 'Accounting Reporting Period & Form',
    field2ValueUk: 'І Квартал 2026 року / Баланс Форма 1-дс та Звіт про фінрезультати Форма 2-дс',
    field2ValuePl: 'I Kwartał 2026 r. / Bilans Formularz 1-ds i RZiS Formularz 2-ds',
    field2ValueEn: 'Q1 2026 / Public Balance Sheet Form 1-ds & Financial Results Form 2-ds',
    field2Value: 'І Квартал 2026 року / Баланс Форма 1-дс та Звіт про фінрезультати Форма 2-дс',

    field3LabelUk: 'Меморіальний ордер для перевірки/складання',
    field3LabelPl: 'Dziennik / Raport memoriałowy (MO)',
    field3LabelEn: 'Memorial Order Journal Code (MO 1–17)',
    field3ValueUk: 'Меморіальний ордер № 9 (Облік матеріальних цінностей та ОЗ) / МО № 6 (Кредитори)',
    field3ValuePl: 'Raport memoriałowy MO nr 9 (Środki trwałe) / MO nr 6 (Rozrachunki)',
    field3ValueEn: 'Memorial Order No. 9 (Fixed Assets Accounting) / MO No. 6 (Creditors)',
    field3Value: 'Меморіальний ордер № 9 (Облік матеріальних цінностей та ОЗ) / МО № 6 (Кредитори)',

    field4LabelUk: 'Первинна вартість & Знос Основних засобів (КЕКВ 3110)',
    field4LabelPl: 'Wartość początkowa i umorzenie środków trwałych',
    field4LabelEn: 'Initial Book Value & Accumulated Depreciation',
    field4ValueUk: 'Первинна вартість ОЗ: 45 800 000 грн, Нарахований знос (НП(С)БОДС 121): 18 200 000 грн',
    field4ValuePl: 'Wartość początkowa: 45.8 mln UAH, Umorzenie: 18.2 mln UAH',
    field4ValueEn: 'Initial Assets Value: 45.8M UAH, Accumulated Depreciation: 18.2M UAH',
    field4Value: 'Первинна вартість ОЗ: 45 800 000 грн, Нарахований знос (НП(С)БОДС 121): 18 200 000 грн',

    field5LabelUk: 'Безоплатно отримане майно / Благодійна допомога',
    field5LabelPl: 'Darowizny rzeczowe & Pomoc humanitarna',
    field5LabelEn: 'Donated Assets / Grant Equipment',
    field5ValueUk: 'Апарат УЗД експертного класу вартістю 1 200 000 грн (Субрахунок 1014 «Машини та обладнання»)',
    field5ValuePl: 'Aparat USG o wartości 1.2 mln UAH (Konto 1014 «Maszyny i urządzenia»)',
    field5ValueEn: 'Expert Ultrasound Scanner valued at 1,200,000 UAH (Sub-account 1014 «Equipment»)',
    field5Value: 'Апарат УЗД експертного класу вартістю 1 200 000 грн (Субрахунок 1014 «Машини та обладнання»)',

    field6LabelUk: 'Стан рахунків у Казначействі (Субрахунки 2311/2313)',
    field6LabelPl: 'Stan kont w Izbie Skarbowej / Kasie Państwowej',
    field6LabelEn: 'State Treasury Account Balances (2311/2313)',
    field6ValueUk: 'Залишок коштів на реєстраційному спецрахунку ДКСУ: 840 000.00 грн',
    field6ValuePl: 'Saldo na koncie specjalnym w Kasie: 840 000.00 UAH',
    field6ValueEn: 'Treasury special account balance: 840,000.00 UAH',
    field6Value: 'Залишок коштів на реєстраційному спецрахунку ДКСУ: 840 000.00 грн',

    notesTextUk: 'Провести детальний аудит бухгалтерського обліку за Національними положеннями (стандартами) бухгалтерського обліку в державному секторі (НП(С)БОДС) та Планом рахунків № 1219 на 2026 рік. Перевірити нарахування амортизації за НП(С)БОДС 121, скласти Меморіальний ордер № 9 та сформувати Баланс (Форма 1-дс) у Excel (.xlsx) та Word (.docx за ДСТУ 4163:2020).',
    notesTextPl: 'Przeprowadzić audyt księgowości budżetowej wg standardów NP(S)BODS i Planu Kont nr 1219. Wygenerować raport memoriałowy MO nr 9 i Bilans 1-ds w Word (.docx).',
    notesTextEn: 'Execute public sector accounting audit under NP(S)BODS standards and General Chart of Accounts No. 1219 for FY 2026. Calculate depreciation under standard 121, compile Memorial Order No. 9 and generate Public Balance Sheet Form 1-ds in Word (.docx DSTU 4163:2020).'
  },
  'budget-payroll-auditor': {
    field1LabelUk: 'Категорія працівників & Галузь ЄТС',
    field1LabelPl: 'Kategoria pracowników & Branża siatki płac',
    field1LabelEn: 'Employee Category & Unified Salary Grid',
    field1ValueUk: 'Педагогічні працівники ліцею (Освіта / Постанова КМУ № 1298)',
    field1ValuePl: 'Nauczyciele liceum (Edukacja / Rozporządzenie KMU nr 1298)',
    field1ValueEn: 'Secondary School Teachers (Education / CMU Resolution No. 1298)',
    field1Value: 'Педагогічні працівники ліцею (Освіта / Постанова КМУ № 1298)',

    field2LabelUk: 'Тарифний розряд за ЄТС (1-25 розряд)',
    field2LabelPl: 'Grupa zaszeregowania w siatce (1-25)',
    field2LabelEn: 'Unified Salary Grid Tariff Rank (1–25)',
    field2ValueUk: '14 тарифний розряд (Вчитель вищої категорії, Посадовий оклад: 7 732.00 грн)',
    field2ValuePl: '14 grupa (Nauczyciel dyplomowany, Płaca zasadnicza: 7 732.00 UAH)',
    field2ValueEn: 'Tariff Rank 14 (Senior Qualified Teacher, Base Salary: 7,732.00 UAH)',
    field2Value: '14 тарифний розряд (Вчитель вищої категорії, Посадовий оклад: 7 732.00 грн)',

    field3LabelUk: 'Педагогічне навантаження (Годин на тиждень)',
    field3LabelPl: 'Wymiar pensum (Godziny tygodniowo)',
    field3LabelEn: 'Teaching Workload (Hours per Week)',
    field3ValueUk: '24 години на тиждень (1.33 ставки при нормі 18 годин)',
    field3ValuePl: '24 godziny tygodniowo (1.33 etatu przy normie 18 h)',
    field3ValueEn: '24 teaching hours / week (1.33 FTE against 18-hour standard)',
    field3Value: '24 години на тиждень (1.33 ставки при нормі 18 годин)',

    field4LabelUk: 'Надбавки (% за вислугу, престижність, ранг)',
    field4LabelPl: 'Dodatki stażowe i motywacyjne (%)',
    field4LabelEn: 'Statutory Allowances (% Seniority, Prestige)',
    field4ValueUk: 'Надбавка за вислугу років (>20 років): 30%, Надбавка за престижність праці: 20%',
    field4ValuePl: 'Wysługa lat (>20 lat): 30%, Dodatek za prestiż: 20%',
    field4ValueEn: 'Seniority allowance (>20 yrs): 30%, Teaching prestige allowance: 20%',
    field4Value: 'Надбавка за вислугу років (>20 років): 30%, Надбавка за престижність праці: 20%',

    field5LabelUk: 'Доплати (Керівництво, зошити, кабінет)',
    field5LabelPl: 'Dodatki funkcyjne (Wychowawstwo, sprawdzanie)',
    field5LabelEn: 'Functional Supplements (Homeroom, Grading)',
    field5ValueUk: 'Доплата за класне керівництво: 20%, Доплата за перевірку зошитів: 15%',
    field5ValuePl: 'Wychowawstwo: 20%, Sprawdzanie zeszytów: 15%',
    field5ValueEn: 'Homeroom supervision: 20%, Homework grading: 15%',
    field5Value: 'Доплата за класне керівництво: 20%, Доплата за перевірку зошитів: 15%',

    field6LabelUk: 'Утримання податків (ПДФО 18%, ВЗ 5%, ЄСВ 22%)',
    field6LabelPl: 'Podatki i potrącenia (PIT 18%, Wojskowy 5%, ZUS 22%)',
    field6LabelEn: 'Tax Withholdings (18% PIT, 5% Military Tax, 22% ESV)',
    field6ValueUk: 'Нараховано: 18 450.00 грн, Утримано ПДФО (18%): 3 321.00 грн, ВЗ (5% Закон № 4015-IX): 922.50 грн, До виплати: 14 206.50 грн',
    field6ValuePl: 'Brutto: 18 450.00 UAH, PIT (18%): 3 321.00 UAH, Składka wojskowa (5%): 922.50 UAH, Netto: 14 206.50 UAH',
    field6ValueEn: 'Gross: 18,450.00 UAH, PIT (18%): 3,321.00 UAH, Military Tax (5% Law No. 4015-IX): 922.50 UAH, Net: 14,206.50 UAH',
    field6Value: 'Нараховано: 18 450.00 грн, Утримано ПДФО (18%): 3 321.00 грн, ВЗ (5% Закон № 4015-IX): 922.50 грн, До виплати: 14 206.50 грн',

    notesTextUk: 'Провести повний перерахунок та аудит заробітної плати педагогічного працівника за Єдиною тарифною сіткою (ЄТС) та Постановою КМУ № 1298 на 2026 рік. Перевірити законність нарахування надбавки за вислугу років, престижність праці, розрахувати утримання ПДФО 18% та військового збору за діючою ставкою 5% (Закон України № 4015-IX), нарахування ЄСВ 22% та сформувати Розрахунково-платіжну відомість і Тарифікаційний список у Word (.docx за ДСТУ 4163:2020) та Excel (.xlsx).',
    notesTextPl: 'Przeprowadzić audyt i kalkulację wynagrodzenia pracownika oświaty wg siatki płac KMU 1298 na 2026 r. Naliczyć PIT 18%, nową stawkę składki wojskowej 5% (Ustawa 4015-IX) i ZUS 22% w Word (.docx) i Excel (.xlsx).',
    notesTextEn: 'Execute payroll computation and compliance audit for education professional under Unified Tariff Grid (ETS) and CMU Res. No. 1298 for FY 2026. Audit seniority allowances, teaching prestige bonus, calculate 18% PIT and new 5% Military Tax rate under Law No. 4015-IX, compute 22% employer ESV and compile Tariff Register in Word (.docx DSTU 4163:2020) and Excel (.xlsx).'
  },
  'dostupni-liky-reimbursement': {
    field1LabelUk: 'Назва аптечного закладу & Договір з НСЗУ', field1Value: 'Аптека № 2 ТОВ «Мед-Фарм» (Договір про реімбурсацію з НСЗУ № 8491/2026)',
    field2LabelUk: 'Звітний місяць & Кількість е-рецептів', field2Value: 'Липень 2026 року / 1 240 електронних рецептів ЕСОЗ',
    field3LabelUk: 'Категорія захворювань (Програма НСЗУ)', field3Value: 'Серцево-судинні захворювання, Бронхіальна астма, Цукровий діабет ІІ типу',
    field4LabelUk: 'Загальна вартість відпущених ЛЗ (грн)', field4Value: '482 400.00 грн (З них сума відшкодування НСЗУ: 398 100.00 грн)',
    field5LabelUk: 'Сума доплати пацієнтами (грн)', field5Value: '84 300.00 грн (В межах граничних торговельних надбавок)',
    field6LabelUk: 'Перевірка граничних роздрібних надбавок (до 15%)', field6Value: 'Надбавка 15% витримана строго за Реєстром граничних цін НСЗУ',
    notesTextUk: 'Провести перевірку та зведення електронних рецептів за програмою реімбурсації «Доступні ліки». Перевірити дотримання граничних роздрібних цін та торговельних надбавок, розрахувати підсумкову суму до виплати від НСЗУ та сформувати готовий Звіт для НСЗУ у Excel (.xlsx) та Word (.docx).'
  }
};

const LOADING_STEPS_UK = [
  "Ініціалізація податкового аналітичного модуля за Податковим кодексом України...",
  "Аналіз наданих первинних документів, виписок та фінансових показників...",
  "Обчислення бази оподаткування, перевірка пільг, ставок та граничних лімітів...",
  "Проведення автоматичного камерального аудиту на логічні та математичні помилки...",
  "Формування Податкової Декларації та підсумкового розрахункового звіту за ДСТУ 4163:2020..."
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Calculator className={className} />;
  return <IconComponent className={className} />;
};

export default function TaxAgentView({
  agentId,
  language,
  onBack,
  onGoHome,
  useEnterpriseContext,
  enterpriseName,
  onLanguageChange
}: TaxAgentViewProps) {
  const [currentLang, setCurrentLang] = useState<'uk' | 'en' | 'pl'>(language);

  useEffect(() => {
    setCurrentLang(language);
  }, [language]);

  const isUk = currentLang === 'uk';
  const isPl = currentLang === 'pl';
  const isEn = currentLang === 'en';
  const agentConfig = agentsConfig.find((a: any) => a.id === agentId) || {
    nameUk: 'Податковий ШІ-Агент',
    nameEn: 'Tax AI Agent',
    descriptionUk: 'Розрахунок податкових зобов\'язань та складання декларацій за ПКУ.',
    descriptionEn: 'Tax liability calculation and declaration filing under the Tax Code.',
    icon: 'Calculator',
    categoryUk: 'ПОДАТКИ & ДЕКЛАРАЦІЇ',
    categoryEn: 'TAXES & DECLARATIONS'
  };

  const defaultDemo = DEFAULT_DEMO_DATA[agentId] || DEFAULT_DEMO_DATA['fop-tax-declarator'];

  const [field1, setField1] = useState(getFieldValue(defaultDemo, 1, currentLang));
  const [field2, setField2] = useState(getFieldValue(defaultDemo, 2, currentLang));
  const [field3, setField3] = useState(getFieldValue(defaultDemo, 3, currentLang));
  const [field4, setField4] = useState(getFieldValue(defaultDemo, 4, currentLang));
  const [field5, setField5] = useState(getFieldValue(defaultDemo, 5, currentLang));
  const [field6, setField6] = useState(getFieldValue(defaultDemo, 6, currentLang));

  const [notesText, setNotesText] = useState(
    useEnterpriseContext 
      ? `${getNotesText(defaultDemo, currentLang)}\n\n[${isPl ? 'Automatycznie załadowano dane podatkowe firmy' : isUk ? 'Автоматично завантажено податкові дані компанії' : 'Auto-loaded company tax data'}: ${enterpriseName || 'Enterprise'}]`
      : getNotesText(defaultDemo, currentLang)
  );

  const [uploadingFile, setUploadingFile] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<Array<{ name: string; text: string }>>([]);

  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<string | null>(null);
  const [copiedReport, setCopiedReport] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [isRegistryModalOpen, setIsRegistryModalOpen] = useState(false);
  const [registryCodePrefill, setRegistryCodePrefill] = useState('');
  const [checkingRates, setCheckingRates] = useState(false);
  const [ratesCheckResult, setRatesCheckResult] = useState<string | null>(null);

  const handleCheckRates = async () => {
    setCheckingRates(true);
    setRatesCheckResult(null);
    try {
      const res = await fetch('/api/tax/check-statutory-rates', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setRatesCheckResult(isUk ? '✅ Нормативи актуальні: МЗП 8 647 грн, ЄСВ 1 902.34 грн' : '✅ Rates up to date: Min Wage 8,647 UAH, ESV 1,902.34 UAH');
      } else {
        setRatesCheckResult(isUk ? '⚠️ Помилка перевірки оновлення' : 'Update check failed');
      }
      setTimeout(() => setRatesCheckResult(null), 5000);
    } catch {
      setRatesCheckResult(isUk ? '⚠️ Помилка з\'єднання' : 'Connection error');
      setTimeout(() => setRatesCheckResult(null), 4000);
    } finally {
      setCheckingRates(false);
    }
  };

  useEffect(() => {
    const demo = DEFAULT_DEMO_DATA[agentId] || DEFAULT_DEMO_DATA['fop-tax-declarator'];
    setField1(getFieldValue(demo, 1, currentLang));
    setField2(getFieldValue(demo, 2, currentLang));
    setField3(getFieldValue(demo, 3, currentLang));
    setField4(getFieldValue(demo, 4, currentLang));
    setField5(getFieldValue(demo, 5, currentLang));
    setField6(getFieldValue(demo, 6, currentLang));
    const rawNotes = getNotesText(demo, currentLang);
    setNotesText(
      useEnterpriseContext 
        ? `${rawNotes}\n\n[${isPl ? 'Automatycznie załadowano dane podatkowe firmy' : isUk ? 'Автоматично завантажено податкові дані компанії' : 'Auto-loaded company tax data'}: ${enterpriseName || 'Enterprise'}]`
        : rawNotes
    );
    setError(null);
    setReport(null);
  }, [agentId, currentLang, useEnterpriseContext, enterpriseName]);

  useEffect(() => {
    if (report && typeof report === 'string' && report.trim().length > 0 && !report.startsWith('⚠️ ПОМИЛКА')) {
      autoDownloadReport(report, agentConfig.nameUk || 'Податковий Звіт', agentId);
    }
  }, [report]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isProcessing) {
      setLoadingStepIndex(0);
      interval = setInterval(() => {
        setLoadingStepIndex((prev) => (prev < LOADING_STEPS_UK.length - 1 ? prev + 1 : prev));
      }, 6000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing]);

  const handleFillDemo = () => {
    const demo = DEFAULT_DEMO_DATA[agentId] || DEFAULT_DEMO_DATA['fop-tax-declarator'];
    setField1(getFieldValue(demo, 1, currentLang));
    setField2(getFieldValue(demo, 2, currentLang));
    setField3(getFieldValue(demo, 3, currentLang));
    setField4(getFieldValue(demo, 4, currentLang));
    setField5(getFieldValue(demo, 5, currentLang));
    setField6(getFieldValue(demo, 6, currentLang));
    const rawNotes = getNotesText(demo, currentLang);
    setNotesText(
      useEnterpriseContext 
        ? `${rawNotes}\n\n[${isPl ? 'Automatycznie załadowano dane podatkowe firmy' : isUk ? 'Автоматично завантажено податкові дані компанії' : 'Auto-loaded company tax data'}: ${enterpriseName || 'Enterprise'}]`
        : rawNotes
    );
    setError(null);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingFile(true);
    setError(null);

    const fileList = Array.from(files) as File[];
    const newAttached: Array<{ name: string; text: string }> = [];

    for (const file of fileList) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch('/api/extract-text', {
          method: 'POST',
          body: formData
        });

        if (res.ok) {
          const data = await res.json();
          const extracted = data.text || '';
          newAttached.push({ name: file.name, text: extracted });

          setNotesText(prev => {
            const header = `--- ЗМІСТ ЗЧИТАНОГО ПОДАТКОВОГО ДОКУМЕНТА (${file.name}) ---`;
            return prev ? `${prev}\n\n${header}\n${extracted}` : `${header}\n${extracted}`;
          });
        }
      } catch (err) {
        console.error(`File extraction error for ${file.name}:`, err);
      }
    }

    setAttachedFiles(prev => [...prev, ...newAttached]);
    setUploadingFile(false);
    e.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRunAgent = async () => {
    if (!field1.trim() && !notesText.trim()) {
      setError(isUk ? 'Введіть основні податкові показники або опис первинних даних.' : 'Please enter key tax parameters or notes.');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setReport(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const filesSummary = attachedFiles.length > 0
        ? `ПРИКРІПЛЕНІ ДОКУМЕНТИ (${attachedFiles.length}): ${attachedFiles.map(f => f.name).join(', ')}`
        : 'Прикріплені документи відсутні';

      const inputPrompt = `
ПОДАТКОВІ ТА БУХГАЛТЕРСЬКІ ВХІДНІ ДАНІ ДЛЯ ДЕКЛАРАЦІЇ ПКУ:
--------------------------------------------------------
1. ${defaultDemo.field1LabelUk}: ${field1}
2. ${defaultDemo.field2LabelUk}: ${field2}
3. ${defaultDemo.field3LabelUk}: ${field3}
4. ${defaultDemo.field4LabelUk}: ${field4}
5. ${defaultDemo.field5LabelUk}: ${field5}
6. ${defaultDemo.field6LabelUk}: ${field6}

${filesSummary}

ПЕРВИННІ ДОКУМЕНТИ, ВИПИСКИ ТА ДЕТАЛІ ОПОДАТКУВАННЯ:
${notesText}

ВИМОГИ ДО РЕЗУЛЬТАТУ:
- Здійснити повний камеральний аудит та розрахунок податкових зобов'язань за Податковим кодексом України (ПКУ).
- Сформувати проєкт офіційної Податкової Декларації / Звіту та платіжні реквізити (IBAN / БКД).
- Оформити підсумковий податковий звіт суворо за стандартами діловодства ДСТУ 4163:2020.
`;

      const response = await fetch('/api/run-templated-crew', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          crewName: agentId,
          agentId: agentId,
          inputs: {
            agent_id: agentId,
            task_description: inputPrompt,
            agent_name: isPl ? (agentConfig.namePl || agentConfig.nameUk) : isEn ? (agentConfig.nameEn || agentConfig.nameUk) : agentConfig.nameUk,
            target_language: currentLang
          },
          useEnterpriseContext
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isUk ? 'Помилка розрахунку податків.' : 'Tax calculation failed.'));
      }

      const data = await response.json();
      
      if (data.jobId) {
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await fetch(`/api/crew-status/${data.jobId}`);
            if (statusRes.ok) {
              const statusData = await statusRes.json();
              if (statusData.status === 'completed') {
                clearInterval(pollInterval);
                setReport(statusData.result || statusData.report);
                setIsProcessing(false);
              } else if (statusData.status === 'failed') {
                clearInterval(pollInterval);
                setError(statusData.error || (isUk ? 'Помилка виконання завдання.' : 'Job execution failed.'));
                setIsProcessing(false);
              }
            }
          } catch (pErr) {
            console.error('Polling error:', pErr);
          }
        }, 3000);
      } else if (data.result || data.report) {
        setReport(data.result || data.report);
        setIsProcessing(false);
      } else {
        throw new Error(isUk ? 'Неотримано результат від бекенду.' : 'No result received from backend.');
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Сталася помилка при запуску.' : 'An error occurred.'));
      setIsProcessing(false);
    }
  };

  const handleCopy = () => {
    if (!report) return;
    navigator.clipboard.writeText(report);
    setCopiedReport(true);
    setTimeout(() => setCopiedReport(false), 2000);
  };

  const handleDownloadDocx = async () => {
    if (!report) return;
    setDownloadingDocx(true);
    try {
      const res = await fetch('/api/convert-md-to-docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          markdownText: report,
          title: agentConfig.nameUk,
          author: 'Deep Research Hub — Податковий Центр ДПС'
        })
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Податкова_Декларація_${agentId}_2026.docx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        alert(isUk ? 'Помилка при генерації Word файлу' : 'Error generating Word file');
      }
    } catch (err) {
      console.error(err);
      alert(isUk ? 'Помилка завантаження' : 'Download error');
    } finally {
      setDownloadingDocx(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in pb-12 font-mono max-w-5xl mx-auto">
      {/* Navigation Header */}
      <div className="flex items-center justify-between gap-4 bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <div className="flex items-center gap-3">
          <button
            onClick={onGoHome || onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
          >
            🏠 {isPl ? 'Strona Główna' : isUk ? 'На головну' : 'Home'}
          </button>
          <button
            onClick={onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{isPl ? '← Powrót do Centrum Podatkowego' : isUk ? '← До Податкового Центру' : '← Back to Tax Hub'}</span>
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* In-agent Language Switcher */}
          <div className="flex items-center bg-slate-800 border border-slate-700 rounded-lg p-0.5 shadow-xs">
            <button
              type="button"
              onClick={() => {
                setCurrentLang('uk');
                if (onLanguageChange) onLanguageChange('uk');
              }}
              className={`px-2 py-1 text-[10px] font-bold rounded-md transition-all cursor-pointer ${
                currentLang === 'uk' ? 'bg-emerald-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'
              }`}
            >
              🇺🇦 UA
            </button>
            <button
              type="button"
              onClick={() => {
                setCurrentLang('pl');
                if (onLanguageChange) onLanguageChange('pl');
              }}
              className={`px-2 py-1 text-[10px] font-bold rounded-md transition-all cursor-pointer ${
                currentLang === 'pl' ? 'bg-rose-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'
              }`}
            >
              🇵🇱 PL
            </button>
            <button
              type="button"
              onClick={() => {
                setCurrentLang('en');
                if (onLanguageChange) onLanguageChange('en');
              }}
              className={`px-2 py-1 text-[10px] font-bold rounded-md transition-all cursor-pointer ${
                currentLang === 'en' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'
              }`}
            >
              🇬🇧 EN
            </button>
          </div>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-emerald-400" />
            Gemini 3.7 Flash • {isPl ? 'KSeF / Podatki' : isUk ? 'ДПС ПКУ' : 'Tax AI Engine'}
          </span>
        </div>
      </div>

      {/* Agent Hero Banner */}
      <div className="relative bg-gradient-to-r from-slate-950 via-emerald-950 to-slate-900 border border-emerald-500/40 rounded-2xl p-6 text-white shadow-xl overflow-hidden">
        <div className="flex items-start gap-4 z-10 relative">
          <div className="p-3 bg-emerald-500/20 border border-emerald-400/30 rounded-xl text-emerald-400 shrink-0">
            <AgentIcon name={agentConfig.icon} className="w-7 h-7" />
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded">
                {getLocalizedAgentCategory(agentConfig, currentLang)}
              </span>
              <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-blue-500/30 text-blue-300 border border-blue-400/40 rounded">
                {isPl ? 'ROZLICZENIA PODATKOWE 2026 • 100% ZGODNOŚĆ' : isUk ? 'ПКУ 2026 • ОФІЦІЙНА ЗВІТНІСТЬ ДПС' : 'TAX 2026 COMPLIANCE'}
              </span>
            </div>
            <h1 className="text-xl sm:text-2xl font-extrabold uppercase text-white tracking-wide">
              {getLocalizedAgentName(agentConfig, currentLang)}
            </h1>
            <p className="text-xs text-slate-300 max-w-3xl leading-relaxed">
              {getLocalizedAgentDesc(agentConfig, currentLang)}
            </p>
          </div>
        </div>
      </div>

      {/* Ukraine Statutory Standards & Auto-Monitor Banner */}
      <div className="bg-slate-900 border border-emerald-500/30 rounded-xl p-4 text-xs text-slate-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 shadow-md">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-950/80 border border-emerald-500/40 rounded-lg text-emerald-300 font-bold">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span>{isPl ? 'Standardy Państwowe 2026' : isUk ? 'Державні стандарти 2026' : 'State Standards 2026'}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-300 flex-wrap text-[11px] sm:text-xs">
            <span>МЗП: <strong className="text-white">8 647 грн</strong></span>
            <span className="text-slate-600">•</span>
            <span>ЄСВ: <strong className="text-emerald-400">1 902.34 грн/міс</strong> (5 707.02 грн/кв.)</span>
            <span className="text-slate-600">•</span>
            <span>ВЗ 1-2 груп: <strong className="text-amber-400">864.70 грн/міс</strong></span>
            <span className="text-slate-600">•</span>
            <span>Ліміт 3 гр: <strong className="text-blue-400">10 091 049 грн</strong></span>
          </div>
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto justify-end">
          {ratesCheckResult && (
            <span className="text-[11px] font-semibold text-emerald-300 animate-fade-in">
              {ratesCheckResult}
            </span>
          )}
          <button
            type="button"
            onClick={handleCheckRates}
            disabled={checkingRates}
            className="px-3 py-1.5 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 border border-emerald-500/40 rounded-lg text-[11px] font-bold transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
            title={isUk ? 'Автоматичний моніторинг щодня о 08:00 та 20:00' : 'Auto-monitored daily at 08:00 and 20:00'}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${checkingRates ? 'animate-spin' : ''}`} />
            <span>
              {checkingRates 
                ? (isUk ? 'Перевірка...' : 'Checking...') 
                : (isUk ? 'Моніторинг нормативів' : isPl ? 'Monitor stawek' : 'Rates Monitor')}
            </span>
          </button>
        </div>
      </div>

      {/* Interactive Form Card */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-6 shadow-xs">
        <div className="flex items-center justify-between flex-wrap gap-3 pb-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-extrabold text-slate-900 uppercase tracking-wide flex items-center gap-2">
              <Calculator className="w-5 h-5 text-emerald-600" />
              <span>{isPl ? 'Parametry Podatkowe & Wskaźniki Deklaracji' : isUk ? 'Вхідні Податкові Параметри & Показники Декларації' : 'Tax & Declaration Input Parameters'}</span>
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {isPl ? 'Podaj parametry podatku lub załącz wyciągi / faktury' : isUk ? 'Вкажіть специфічні показники для даного податку або завантажте первинні виписки/файли' : 'Specify key tax metrics or upload primary statements/reports'}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => {
                const textToScan = `${field1} ${field6} ${notesText}`;
                const m = textToScan.match(/\b\d{8}\b/) || textToScan.match(/\b\d{10}\b/);
                setRegistryCodePrefill(m ? m[0] : '39485721');
                setIsRegistryModalOpen(true);
              }}
              type="button"
              className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-extrabold uppercase rounded-lg border border-blue-200 transition-colors flex items-center gap-1.5 cursor-pointer"
              title={isUk ? 'Перевірити контрагента за ЄДРПОУ/РНОКПП в офіційних реєстрах України (YouControl / ЄДР / ДПС)' : 'Audit counterparty in Ukrainian registries'}
            >
              <Building2 className="w-4 h-4 text-blue-600" />
              <span>{isPl ? '🔍 Sprawdź kontrahenta' : isUk ? '🔍 Перевірити в ЄДР' : '🔍 Check Counterparty'}</span>
            </button>

            <DpsXmlExportButton
              agentId={agentId}
              field1={field1}
              field2={field2}
              field3={field3}
              field4={field4}
              field5={field5}
              field6={field6}
              notesText={notesText}
              isUk={isUk}
            />

            <button
              onClick={handleFillDemo}
              type="button"
              className="px-3.5 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-extrabold uppercase rounded-lg border border-emerald-200 transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              <Sparkles className="w-4 h-4 text-emerald-600 animate-pulse" />
              <span>{isPl ? '⚡ Wypełnij danymi DEMO 1 kliknięciem' : isUk ? '⚡ Заповнити ДЕМО-даними у 1 клік' : '⚡ Fill 1-Click DEMO Data'}</span>
            </button>
          </div>
        </div>

        {/* Inputs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Field 1 */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-700 uppercase flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-slate-500" />
              <span>{getFieldLabel(defaultDemo, 1, currentLang)}</span>
            </label>
            <input
              type="text"
              value={field1}
              onChange={(e) => setField1(e.target.value)}
              className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all text-slate-900 font-mono"
            />
          </div>

          {/* Field 2 */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-700 uppercase flex items-center gap-1.5">
              <Building2 className="w-4 h-4 text-slate-500" />
              <span>{getFieldLabel(defaultDemo, 2, currentLang)}</span>
            </label>
            <input
              type="text"
              value={field2}
              onChange={(e) => setField2(e.target.value)}
              className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all text-slate-900 font-mono"
            />
          </div>

          {/* Field 3 */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-700 uppercase flex items-center gap-1.5">
              <Coins className="w-4 h-4 text-emerald-600" />
              <span>{getFieldLabel(defaultDemo, 3, currentLang)}</span>
            </label>
            <input
              type="text"
              list="risk-criteria-options"
              value={field3}
              onChange={(e) => setField3(e.target.value)}
              placeholder={isPl ? "Wybierz lub wpisz kryterium ryzyka..." : isUk ? "Оберіть зі списку або введіть пункт критерію ризиковості..." : "Select or type risk criterion..."}
              className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all text-slate-900 font-mono font-bold"
            />
            <datalist id="risk-criteria-options">
              <option value="п. 1 Критеріїв ризиковості операцій (Перевищення обсягу постачання товару над обсягом придбання у віртуальному залишку > 1.5 разу)" />
              <option value="п. 8 Критеріїв ризиковості платника податку (Наявність у контролюючого органу податкової інформації про ризиковість)" />
              <option value="п. 2 Критеріїв ризиковості операцій (Відсутність ліцензій/дозволів на виробництво чи реалізацію товарів)" />
              <option value="п. 4 Критеріїв ризиковості операцій (Складання РК на зміну номенклатури або зменшення суми податкових зобов'язань)" />
              <option value="п. 6 Критеріїв ризиковості платника (Недостатність площ земельних ділянок для задекларованих обсягів с/г продукції)" />
              <option value="п. 5 Критеріїв ризиковості платника (Відсутність банківських рахунків, крім рахунків у Казначействі)" />
            </datalist>
          </div>

          {/* Field 4 */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-700 uppercase flex items-center gap-1.5">
              <Receipt className="w-4 h-4 text-slate-500" />
              <span>{getFieldLabel(defaultDemo, 4, currentLang)}</span>
            </label>
            <input
              type="text"
              value={field4}
              onChange={(e) => setField4(e.target.value)}
              className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all text-slate-900 font-mono"
            />
          </div>

          {/* Field 5 */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-700 uppercase flex items-center gap-1.5">
              <Users className="w-4 h-4 text-slate-500" />
              <span>{getFieldLabel(defaultDemo, 5, currentLang)}</span>
            </label>
            <input
              type="text"
              value={field5}
              onChange={(e) => setField5(e.target.value)}
              className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all text-slate-900 font-mono"
            />
          </div>

          {/* Field 6 */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-700 uppercase flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-slate-500" />
              <span>{getFieldLabel(defaultDemo, 6, currentLang)}</span>
            </label>
            <input
              type="text"
              value={field6}
              onChange={(e) => setField6(e.target.value)}
              className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all text-slate-900 font-mono"
            />
          </div>
        </div>

        {/* Multi-Document File Upload Zone */}
        <div className="space-y-2 pt-2 border-t border-slate-100">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <label className="text-xs font-bold text-slate-700 uppercase flex items-center gap-1.5">
              <Upload className="w-4 h-4 text-emerald-600" />
              <span>{isUk ? '📎 ЗАВАНТАЖЕННЯ ДЕКІЛЬКОХ ДОКУМЕНТІВ / ВИПИСОК (PDF, DOCX, XLSX, CSV, XML)' : 'Upload Multiple Statements / Docs (PDF, DOCX, XLSX, CSV, XML)'}</span>
            </label>
            <span className="text-[10px] text-slate-500">
              {isUk ? 'Можна обирати кілька файлів одночасно' : 'Select multiple files simultaneously'}
            </span>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <label className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 rounded-lg font-bold text-xs uppercase cursor-pointer transition-colors flex items-center gap-2">
              <Upload className="w-4 h-4 text-slate-600" />
              <span>{uploadingFile ? (isUk ? 'Зчитування файлів...' : 'Extracting files...') : (isUk ? '📁 Додати виписки/файли (можна декілька)' : '📁 Attach multiple files')}</span>
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                accept=".xlsx,.xls,.csv,.pdf,.docx,.doc,.xml,.txt,.json,.jpg,.png"
                className="hidden"
                disabled={uploadingFile}
              />
            </label>

            {attachedFiles.map((fileItem, idx) => (
              <div key={idx} className="flex items-center gap-1.5 bg-emerald-50 text-emerald-800 border border-emerald-300 px-3 py-1.5 rounded-lg text-xs font-bold font-mono shadow-xs">
                <FileCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span className="truncate max-w-[200px]">{fileItem.name}</span>
                <button
                  onClick={() => handleRemoveFile(idx)}
                  className="p-0.5 hover:bg-emerald-200/60 rounded text-emerald-700 hover:text-red-600 transition-colors cursor-pointer ml-1"
                  title={isUk ? 'Видалити файл' : 'Remove file'}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Notes Textarea */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-slate-700 uppercase flex items-center gap-1.5">
            <FileText className="w-4 h-4 text-slate-500" />
            <span>{isUk ? 'Первинні Виписки, Деталі Оподаткування та Зміст Файлів' : 'Primary Statements, Tax Details & Files Content'}</span>
          </label>
          <textarea
            rows={5}
            value={notesText}
            onChange={(e) => setNotesText(e.target.value)}
            placeholder="Введіть текстові деталі або завантажте виписку вище..."
            className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all text-slate-900 font-mono leading-relaxed"
          />
        </div>

        {/* Error Alert */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-mono flex items-center gap-3">
            <AlertCircle className="w-5 h-5 shrink-0 text-red-500" />
            <span>{error}</span>
          </div>
        )}

        {/* Submit Action Button */}
        <div className="pt-2 flex items-center justify-between flex-wrap gap-4">
          <p className="text-[11px] text-slate-500 font-mono">
            {isUk ? '⚡ Результат включає обчислення ПКУ, перевірку помилок та ДСТУ-звіт' : '⚡ Result includes Tax Code math, compliance check & DSTU report'}
          </p>

          <button
            onClick={handleRunAgent}
            disabled={isProcessing}
            type="button"
            className="px-6 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-extrabold uppercase rounded-xl transition-all shadow-lg shadow-emerald-600/20 flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {isProcessing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin text-white" />
                <span>{isUk ? 'Проведення аудиту...' : 'Processing...'}</span>
              </>
            ) : (
              <>
                <span>{isUk ? '🚀 Провести Розрахунок & Скласти Декларацію' : '🚀 Calculate Tax & Generate Filing'}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Stepper Status */}
      {isProcessing && (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 text-white space-y-3 font-mono animate-pulse">
          <div className="flex items-center gap-3">
            <RefreshCw className="w-5 h-5 animate-spin text-emerald-400" />
            <span className="text-xs font-extrabold uppercase text-emerald-300">
              {isUk ? 'ШІ-Ядро Gemini 3.7 Flash обробляє дані' : 'Gemini 3.7 Flash tax engine processing'}
            </span>
          </div>
          <p className="text-xs text-slate-300">
            {LOADING_STEPS_UK[loadingStepIndex]}
          </p>
        </div>
      )}

      {/* Report Result */}
      {report && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-4 shadow-sm animate-fade-in font-mono">
          <div className="flex items-center justify-between flex-wrap gap-3 pb-4 border-b border-slate-100">
            <h3 className="text-sm font-extrabold text-slate-900 uppercase tracking-wide flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-emerald-600" />
              <span>{isUk ? 'Результат Розрахунку & Проєкт Декларації ДПС (ДСТУ 4163:2020)' : 'Tax Calculation Result & Declaration Project'}</span>
            </h3>

            <div className="flex items-center flex-wrap gap-2">
              <TelegramExportButton
                title={agentConfig?.nameUk || 'Податковий Розрахунок & Декларація'}
                content={typeof report === 'string' ? report : String(report)}
                agentName={agentConfig?.nameUk || 'Податковий Консультант'}
                agentId={agentId}
                isUk={isUk}
                variant="emerald"
              />

              <ExcelExportButton
                title={agentConfig?.nameUk || 'Податковий Розрахунок'}
                content={typeof report === 'string' ? report : String(report)}
                fileName={agentId || 'tax_calculation'}
                isUk={isUk}
                variant="primary"
              />

              <DpsXmlExportButton
                agentId={agentId}
                field1={field1}
                field2={field2}
                field3={field3}
                field4={field4}
                field5={field5}
                field6={field6}
                notesText={notesText}
                isUk={isUk}
              />

              <button
                onClick={handleCopy}
                className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer border border-slate-200"
              >
                {copiedReport ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                <span>{copiedReport ? (isUk ? 'Скопійовано!' : 'Copied!') : (isUk ? 'Скопійовати' : 'Copy')}</span>
              </button>

              <button
                onClick={handleDownloadDocx}
                disabled={downloadingDocx}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-extrabold uppercase rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer border border-slate-700 shadow-xs disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                <span>{downloadingDocx ? (isUk ? 'Генерація...' : 'Generating...') : (isUk ? 'Word (.docx)' : 'Word (.docx)')}</span>
              </button>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-xs leading-relaxed text-slate-800">
            <ClickableReportViewer content={report} lightMode={true} />
          </div>
        </div>
      )}

      {/* Counterparty Registry Modal */}
      <CounterpartyCheckerModal
        isOpen={isRegistryModalOpen}
        onClose={() => setIsRegistryModalOpen(false)}
        initialCode={registryCodePrefill}
        isUk={isUk}
        onSelectDossier={(dossierText) => {
          setNotesText((prev) => prev ? `${prev}\n\n${dossierText}` : dossierText);
          setIsRegistryModalOpen(false);
        }}
      />
    </div>
  );
}
